/**
 * The Color — Cart JavaScript
 * Full AJAX cart: add to cart, update quantity, remove item, cart drawer open/close,
 * cart count badge update, free shipping threshold calculation.
 * Uses Shopify /cart.js, /cart/add.js, /cart/change.js endpoints.
 */

(function () {
  'use strict';

  /**
   * Build a Shopify CDN URL with a specific width parameter.
   * Handles both legacy _600x suffix URLs and modern ?width= URLs.
   */
  function getSizedImageUrl(src, width) {
    if (!src) return '';
    var url = src.replace(/_\d+x(\d+)?(?=\.|$)/g, '');
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', width);
      /* Return full URL if CDN origin differs from current page */
      if (parsed.origin !== window.location.origin) {
        return parsed.href;
      }
      return parsed.pathname + parsed.search;
    } catch (e) {
      url = url.replace(/[?&]width=\d+/g, '');
      var sep = url.indexOf('?') !== -1 ? '&' : '?';
      return url + sep + 'width=' + width;
    }
  }

  /* --------------------------------------------------------------------------
     1. Configuration & Selectors
     -------------------------------------------------------------------------- */

  const config = {
    freeShippingThreshold: 3000, // £30.00 in pence — fallback only; updated dynamically from theme settings
  };

  /* Subscribe & save delivery wording (owner, 13 Oct 2026; 19 Oct: every
     subscription delivery ships free in any basket). The same words are in
     snippets/subscription-delivery.liquid (the first paint), so change both
     together. See CartRenderer.subsInfo() for when each shows. */
  const SUBS_COPY = {
    barSub: 'Free UK delivery on every subscription delivery',
    barMixedFree: 'Free UK delivery on this order and every subscription delivery',
    /* 'mixed' (one-off items too, basket under the threshold): follows
       "… more for free delivery" */
    mixedOneOff: ' on your one-off items',
    mixedTail: '. Your subscription ships free every time.',
    lineFree: 'free delivery every time',
  };

  const selectors = {
    cartDrawer: '[data-cart-drawer]',
    cartDrawerClose: '[data-action="close-cart"]',
    cartDrawerOpen: '[data-action="open-cart"]',
    cartCount: '[data-cart-count], [data-cart-drawer-count]',
    cartItems: '[data-cart-items]',
    cartSubtotal: '[data-cart-subtotal]',
    cartEmpty: '[data-cart-empty]',
    cartFooter: '[data-cart-footer]',
    shippingBar: '[data-cart-shipping-bar]',
    shippingProgress: '[data-shipping-fill]',
    shippingMessage: '[data-shipping-text]',
    addToCart: '[data-add-to-cart]',
    lineItemRemove: '[data-action="remove-item"]',
    lineItemQuantity: '[data-quantity-input]',
    lineItemMinus: '[data-action="decrease-quantity"]',
    lineItemPlus: '[data-action="increase-quantity"]',
  };

  /* --------------------------------------------------------------------------
     2. Cart State
     -------------------------------------------------------------------------- */

  let isUpdating = false;

  /* Serial queue for ALL cart mutations — adds, quantity changes and removes.
     Adds are keyed by variant id, and quantity/remove are addressed by line-item
     KEY (see CartAPI.change), so none of them depend on a line index that could
     shift while queued. Sharing one queue also guarantees mutations never
     interleave, which is what `isUpdating` alone used to provide. */
  let addQueue = Promise.resolve();

  /* Bumped by every mutation. init() captures it before its GET and refuses to
     paint if it changed, because `isUpdating` alone leaves a window: init can
     issue its GET, a whole add can start AND finish (clearing the flag), and the
     older in-flight response then lands and repaints the pre-add cart — wiping a
     line the shopper just added. */
  let cartGeneration = 0;

  /* --------------------------------------------------------------------------
     3. Shopify Cart API
     -------------------------------------------------------------------------- */

  /* Time-boxed fetch for cart requests. Every mutation runs through the one
     serial queue (addQueue), so a single request that never settles — a dead
     mobile connection mid-flight is the realistic case — would otherwise block
     every later add, quantity change and remove for the life of the page with
     no feedback. Aborting after 10s lets the rejection flow down the existing
     catch paths, which show the cart:error toast and re-sync the UI. The
     begin_checkout beacon is untouched: it goes via gtag, not fetch. */
  const FETCH_TIMEOUT_MS = 10000;

  async function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      return await fetch(url, Object.assign({}, options, { signal: controller.signal }));
    } catch (error) {
      /* The abort rejects with a DOMException whose message is browser jargon
         ("signal is aborted without reason") — the toast shows error.message,
         so replace it with something a shopper can act on. */
      if (error && error.name === 'AbortError') {
        const timeout = new Error('The request timed out. Please check your connection and try again.');
        /* Flagged so the mutation paths can re-read /cart.js before showing
           an error: a slow request that DID land must not be reported as a
           failure (round2 robustness: timeout false failures). */
        timeout.isTimeout = true;
        throw timeout;
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  const CartAPI = {
    /**
     * Fetch current cart state
     */
    async get() {
      const response = await fetchWithTimeout('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to fetch cart');
      return response.json();
    },

    /**
     * Add item to cart. `sellingPlan` (Subscribe & save, S1) is sent only
     * when it is a real plan id; a one-off add posts exactly what it always
     * did, with no selling_plan key at all.
     */
    async add(variantId, quantity = 1, properties = {}, sellingPlan = null) {
      const item = {
        id: variantId,
        quantity: quantity,
        properties: properties,
      };
      const plan = parseInt(sellingPlan, 10);
      if (plan > 0) item.selling_plan = plan;
      const response = await fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [item],
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }
      return response.json();
    },

    /**
     * Update item quantity by line index (1-based)
     */
    /**
     * Update a line's quantity.
     * `target` is either a line-item KEY (string, preferred) or a 1-based line
     * index (number). Keys are stable, indices are not: Shopify prepends new
     * lines, so an index captured at click time can point at a different product
     * by the time a queued request runs.
     */
    async change(target, quantity, sections) {
      const body = typeof target === 'string'
        ? { id: target, quantity }
        : { line: target, quantity };
      /* Bundled Section Rendering: /cart passes its section id so the same
         round trip returns its fresh HTML (cart.sections[id]). */
      if (sections) body.sections = sections;
      const response = await fetchWithTimeout('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('Failed to update cart');
      return response.json();
    },

    /**
     * POST /cart/update.js with any payload ({note}, {discount}, …).
     * Resolves with the cart JSON; rejects with the store's message.
     */
    async update(payload, options = {}) {
      const response = await fetchWithTimeout('/cart/update.js', Object.assign({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }, options));
      if (!response.ok) {
        let msg = 'Failed to update cart';
        try { const err = await response.json(); msg = err.description || err.message || msg; } catch (e) {}
        throw new Error(msg);
      }
      return response.json();
    },

  };

  /* --------------------------------------------------------------------------
     3b. Dispatch clock — shared same-day helper (TheColor.Dispatch)

     One place that knows the cut-off, the closed dates and the holiday pause,
     so the drawer, /cart and the product page can't disagree. It reads the
     Theme settings that layout/theme.liquid exposes (B01), first match wins:
       • cut-off: theme.cutoffHour → the countdown element's
         data-dispatch-cutoff (settings.dispatch_cutoff, e.g. "2pm") → 14:00;
       • dispatch day: theme.isDispatchDay(iso) (Mon–Fri, not in
         theme.closedDates, not inside the holiday pause), and never one of
         the built-in England & Wales bank holidays below (a safety net for
         years the owner's list doesn't reach yet). Without the layout helper
         it rebuilds the same rule from theme.closedDates (or the element's
         data-dispatch-closed) and theme.isDispatchPaused.
     status() → { paused, sameDay, minsLeft, today, next } in Europe/London;
     `next` is the next real dispatch day (after the holiday pause).
     If another script defined TheColor.Dispatch with its own status(), the
     cart uses that one (same shape) and never overwrites it.
     -------------------------------------------------------------------------- */

  const LocalDispatch = (function () {
    /* England & Wales bank holidays (gov.uk), including substitute days.
       Royal Mail doesn't collect on these. The owner's list (Theme settings
       → Delivery → No-dispatch dates) is the place to add years; this list
       only stops a same-day promise on a bank holiday it doesn't cover. */
    const BANK_HOLIDAYS = [
      '2026-08-31', '2026-12-25', '2026-12-28',
      '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
      '2028-01-03', '2028-04-14', '2028-04-17', '2028-05-01', '2028-05-29', '2028-08-28', '2028-12-25', '2028-12-26',
    ];
    const ISO = /^\d{4}-\d{2}-\d{2}$/;
    const pad = (n) => String(n).padStart(2, '0');
    const toIso = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
    /* Local noon of a YYYY-MM-DD (no string parsing: older Safari reads
       "YYYY-MM-DDTHH:mm" as UTC). */
    const fromIso = (iso) => { const p = iso.split('-'); return new Date(+p[0], +p[1] - 1, +p[2], 12); };

    /* A Date whose LOCAL fields are the Europe/London wall clock. Built from
       formatToParts (re-parsing a locale string is unreliable in Safari). */
    function londonNow() {
      const now = new Date();
      try {
        const p = {};
        new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London', hourCycle: 'h23',
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).formatToParts(now).forEach((x) => { p[x.type] = x.value; });
        const d = new Date(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
        if (!isNaN(d.getTime())) return d;
      } catch (e) { /* no Intl time zones: the device clock is the best guess */ }
      return now;
    }

    /* 14, "14", "2pm", "2 pm", "2.30pm", "14:00", "12pm" → minutes after midnight. */
    function parseCutoff(v) {
      if (v === null || v === undefined || v === '') return null;
      if (typeof v === 'number' && isFinite(v)) return v >= 0 && v < 24 ? Math.round(v * 60) : null;
      const m = /^\s*(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?\s*$/i.exec(String(v));
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = m[2] ? parseInt(m[2], 10) : 0;
      const ap = m[3] ? m[3].toLowerCase() : '';
      if (ap === 'pm' && h < 12) h += 12;
      if (ap === 'am' && h === 12) h = 0;
      if (h > 23 || min > 59) return null;
      return h * 60 + min;
    }

    function attr(name) {
      const el = document.querySelector('[' + name + ']');
      return el ? el.getAttribute(name) : null;
    }

    function cutoffMinutes() {
      const t = window.theme || {};
      let m = parseCutoff(t.cutoffHour);
      if (m === null) m = parseCutoff(attr('data-dispatch-cutoff'));
      return m === null ? 14 * 60 : m;
    }

    /* The owner's no-dispatch dates: theme.closedDates, else the countdown
       element's data-dispatch-closed (the raw setting). */
    function ownerClosed() {
      const t = window.theme || {};
      const list = Array.isArray(t.closedDates) ? t.closedDates : String(attr('data-dispatch-closed') || '').split(/[\s,;]+/);
      return list.map((x) => String(x || '').trim()).filter((x) => ISO.test(x));
    }

    /* Every no-dispatch date: the owner's list plus the built-in bank holidays. */
    function closedDates() {
      const own = ownerClosed();
      return own.concat(BANK_HOLIDAYS.filter((d) => own.indexOf(d) === -1));
    }

    function isPaused(iso) {
      const fn = window.theme && window.theme.isDispatchPaused;
      try { return typeof fn === 'function' ? !!fn(iso) : false; } catch (e) { return false; }
    }

    /* Mon–Fri and not a closed date — the holiday pause is NOT applied here
       (status() needs to see a paused day as "would have been open"). */
    function isWorkingDay(iso, closed) {
      const day = fromIso(iso).getDay();
      return day >= 1 && day <= 5 && (closed || closedDates()).indexOf(iso) === -1;
    }

    /* Orders actually go out that day: theme.isDispatchDay (weekday, owner's
       closed dates, holiday pause) and not a built-in bank holiday. */
    function isDispatchDay(iso, closed) {
      const fn = window.theme && window.theme.isDispatchDay;
      if (typeof fn === 'function') {
        try {
          return !!fn(iso) && (closed || closedDates()).indexOf(iso) === -1;
        } catch (e) { /* fall through to the same rule rebuilt here */ }
      }
      return isWorkingDay(iso, closed) && !isPaused(iso);
    }

    /* First day strictly after `iso` that passes `test` (looks 60 days
       ahead, so a long holiday pause still finds its end). */
    function nextDay(iso, test, closed) {
      const d = fromIso(iso);
      for (let k = 0; k < 60; k++) {
        d.setDate(d.getDate() + 1);
        const s = toIso(d);
        if (test(s, closed)) return s;
      }
      return '';
    }
    const nextWorkingDay = (iso, closed) => nextDay(iso, isWorkingDay, closed || closedDates());
    const nextDispatchDay = (iso, closed) => nextDay(iso, isDispatchDay, closed || closedDates());

    function status(at) {
      const now = at || londonNow();
      const today = toIso(now);
      const closed = closedDates();
      const cut = cutoffMinutes();
      const mins = now.getHours() * 60 + now.getMinutes();
      const beforeCut = isDispatchDay(today, closed) && mins < cut;
      /* Same rule as before: the pause hides the countdown from its first
         day, and from the cut-off of the working day before it. */
      const nextOpen = beforeCut ? today : nextWorkingDay(today, closed);
      const paused = isPaused(today) || (!beforeCut && !!nextOpen && isPaused(nextOpen));
      return {
        paused: paused,
        sameDay: beforeCut && !paused,
        minsLeft: beforeCut ? cut - mins : 0,
        today: today,
        next: beforeCut ? today : (nextDispatchDay(today, closed) || nextOpen),
        cutoffMinutes: cut,
      };
    }

    return {
      status, isDispatchDay, isWorkingDay, nextDispatchDay, nextWorkingDay,
      closedDates, cutoffMinutes, parseCutoff, londonNow, toIso, isPaused,
    };
  })();

  /* The helper the cart uses: a foreign TheColor.Dispatch only if it speaks
     the same status() shape, else ours. */
  function dispatchStatus() {
    const ext = window.TheColor && window.TheColor.Dispatch;
    if (ext && ext !== LocalDispatch && typeof ext.status === 'function') {
      try {
        const r = ext.status();
        if (r && typeof r.sameDay === 'boolean' && typeof r.paused === 'boolean') return r;
      } catch (e) { /* fall through to ours */ }
    }
    return LocalDispatch.status();
  }

  /* --------------------------------------------------------------------------
     4. Cart Drawer UI
     -------------------------------------------------------------------------- */

  /* Where focus goes back to when the drawer closes (QA Q3-03 / Q5-03).
     document.activeElement at open() is often useless: product.js and the
     quick-add disable their button for the add (so focus has already fallen
     to <body>), and the card size picker removes the option that was
     pressed. So: remember the last control focused or pressed OUTSIDE the
     drawer, let addToCart hand its trigger to open(), and at close() walk
     a short list of stand-ins when that control is gone. */
  const FOCUSABLE_SEL = 'a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex]';
  let lastOutsideFocus = null;
  function isPageControl(el) {
    if (!el || el.nodeType !== 1 || el === document.body || el === document.documentElement) return false;
    const d = CartDrawer.drawer;
    return !(d && d.contains(el));
  }
  document.addEventListener('focusin', function (e) {
    if (isPageControl(e.target)) lastOutsideFocus = e.target;
  }, true);
  /* Safari doesn't focus a clicked button: count presses too. */
  document.addEventListener('pointerdown', function (e) {
    const t = e.target && e.target.closest ? e.target.closest(FOCUSABLE_SEL) : null;
    if (isPageControl(t)) lastOutsideFocus = t;
  }, true);
  function captureOpener() {
    const a = document.activeElement;
    return isPageControl(a) ? a : lastOutsideFocus;
  }
  /* Focusable right now, on the page (not in the drawer). `tabbable` also
     asks for a Tab stop (the stand-ins; the original may be a tabindex=-1
     heading that was focused on purpose). */
  function canFocus(el, tabbable) {
    if (!isPageControl(el) || !el.isConnected || el.disabled) return false;
    if (tabbable && el.tabIndex < 0) return false;
    if (el.closest('[inert], [hidden], [aria-hidden="true"]')) return false;
    if (!el.getClientRects().length) return false;
    return getComputedStyle(el).visibility !== 'hidden';
  }

  const CartDrawer = {
    drawer: null,
    releaseFocus: null,
    previousFocus: null,
    returnHints: null,

    init() {
      this.drawer = document.querySelector(selectors.cartDrawer);
      if (!this.drawer) return;

      // Open buttons
      document.querySelectorAll(selectors.cartDrawerOpen).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Close button
      const closeBtn = this.drawer.querySelector(selectors.cartDrawerClose);
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });

      // Overlay click (dispatched from theme.js)
      document.addEventListener('overlay:clicked', () => {
        if (this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });

      // Read free shipping threshold from global theme settings (set in theme.liquid)
      if (window.theme && window.theme.freeShippingThreshold) {
        config.freeShippingThreshold = window.theme.freeShippingThreshold;
      }
    },

    open(opts) {
      if (!this.drawer) return;

      /* Re-opening an already-open drawer is a no-op, not a repeat of the whole
         open sequence. Every upsell "+ Add" routes through addToCart -> open(),
         and without this guard each one overwrote previousFocus (so closing
         restored the wrong element), re-locked scroll, installed a second focus
         trap while orphaning the previous release function, and fired a duplicate
         GA4 view_cart. The upsell still refreshes — CartRenderer.render emits
         'cart:updated' independently of this event. */
      if (this.drawer.classList.contains('is-open')) return;

      this.previousFocus = (opts && isPageControl(opts.returnTo) && opts.returnTo) || captureOpener();
      this.returnHints = this.collectHints(this.previousFocus);
      this.drawer.classList.add('is-open');

      // Fire view_cart event for GA4.
      /* Never a GET of its own: the render cache holds the cart the drawer
         is showing (every render stores it, empty or not). Before the first
         render (a very fast open on a cold page) wait for cart.js's own
         page-load sync instead of racing it with a duplicate /cart.js.
         An empty cart fires nothing. Cross-tab staleness is covered by the
         visibilitychange re-sync below. */
      const lastCart = CartRenderer._lastCart;
      if (lastCart) {
        if (lastCart.items && lastCart.items.length) GtagEvents.viewCart(lastCart);
      } else {
        CartActions.current().then(function (cart) {
          if (cart && cart.items && cart.items.length) GtagEvents.viewCart(cart);
        }).catch(function () {});
      }

      // Let Overlay handle scroll-lock (saves scroll position BEFORE
      // adding position:fixed, then restores it on close).
      if (window.TheColor && window.TheColor.Overlay) {
        window.TheColor.Overlay.show();
      }

      // Trap focus
      if (window.TheColor && window.TheColor.trapFocus) {
        this.releaseFocus = window.TheColor.trapFocus(this.drawer);
      }

      // Notify listeners (cart-drawer upsell loader) that the drawer opened.
      // The "Complete Your Routine" upsell binds to this event; without the
      // dispatch it never loaded.
      document.dispatchEvent(new CustomEvent('cart:open'));
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');

      // Let Overlay handle scroll-unlock (removes drawer-open class,
      // clears body.style.top, and restores scroll position).
      if (window.TheColor && window.TheColor.Overlay) {
        window.TheColor.Overlay.hide();
      }

      if (this.releaseFocus) this.releaseFocus();
      this.setNotice('');
      this.restoreFocus();
    },

    /* Stand-ins for the opener, worked out at open() while the page is as
       the shopper left it: the control that opened a popup the opener sat
       in (aria-controls — the card's "Choose size" button for a picker
       option), then its nearest surviving container's first Tab stop (a
       re-rendered list), then the header cart button. */
    collectHints(el) {
      const controllers = [];
      const ancestors = [];
      for (let n = el && el.parentElement, i = 0; n && n !== document.body && i < 8; n = n.parentElement, i++) {
        ancestors.push(n);
        if (n.id) {
          let ctl = null;
          try {
            ctl = document.querySelector('[aria-controls~="' + (window.CSS && CSS.escape ? CSS.escape(n.id) : n.id) + '"]');
          } catch (e) { /* odd id */ }
          if (ctl && !n.contains(ctl)) controllers.push(ctl);
        }
      }
      return { controllers: controllers, ancestors: ancestors };
    },

    restoreFocus() {
      const target = this.previousFocus;
      const hints = this.returnHints || { controllers: [], ancestors: [] };
      this.previousFocus = null;
      this.returnHints = null;
      const fallback = () => {
        for (let i = 0; i < hints.controllers.length; i++) {
          if (canFocus(hints.controllers[i], true)) { hints.controllers[i].focus(); return; }
        }
        let scanned = 0;
        for (let i = 0; i < hints.ancestors.length && scanned < 3; i++) {
          const a = hints.ancestors[i];
          if (!a.isConnected || a === document.body || a.tagName === 'MAIN') continue;
          scanned++;
          const el = Array.prototype.find.call(a.querySelectorAll(FOCUSABLE_SEL), (c) => canFocus(c, true));
          if (el) { el.focus(); return; }
        }
        const icon = Array.prototype.find.call(document.querySelectorAll(selectors.cartDrawerOpen), (c) => canFocus(c, true));
        if (icon) icon.focus();
      };
      if (canFocus(target, false)) { target.focus(); return; }
      if (target && target.isConnected && target.disabled) {
        /* A busy Add button (product.js keeps it disabled ~2 s on "Added ✓").
           Wait for it rather than land somewhere else — unless the shopper
           moves on first. */
        const t0 = Date.now();
        const drawer = this.drawer;
        const tick = () => {
          const a = document.activeElement;
          /* Focus still on the closing drawer's own Close button (an Escape
             within ~2 s of the add) is not the shopper moving on: it is about
             to fall to <body> as the drawer hides, so keep waiting. */
          if (a && a !== document.body && a !== document.documentElement && !(drawer && drawer.contains(a))) return;
          if (canFocus(target, false)) { target.focus(); return; }
          if (!target.isConnected || Date.now() - t0 > 2500) { fallback(); return; }
          setTimeout(tick, 100);
        };
        tick();
        return;
      }
      fallback();
    },

    /* A short note at the top of the drawer body (partial adds). */
    setNotice(text) {
      const n = this.drawer && this.drawer.querySelector('[data-cart-notice]');
      if (!n) return;
      n.textContent = text || '';
      n.hidden = !text;
    },
  };

  /* --------------------------------------------------------------------------
     5. Cart Rendering
     -------------------------------------------------------------------------- */

  const CartRenderer = {
    formatMoney(pence) {
      return window.TheColor.utils.formatMoney(pence);
    },

    /**
     * Update cart count badges
     */
    updateCount(count) {
      document.querySelectorAll(selectors.cartCount).forEach((el) => {
        /* Read previous count so we can detect "item added" and trigger the
           bounce animation. Using textContent on the visible numeric target
           keeps this resilient to the el.textContent / aria-hidden split. */
        const numericTarget = el.querySelector('[aria-hidden="true"]');
        const readEl = numericTarget || el;
        const previousText = (readEl.textContent || '').replace(/[^\d]/g, '');
        const previousCount = parseInt(previousText, 10) || 0;

        const isDrawerCount = el.hasAttribute('data-cart-drawer-count');
        if (numericTarget) {
          numericTarget.textContent = isDrawerCount ? `(${count})` : count;
        } else {
          /* The drawer counter has no inner aria-hidden span, so it always lands
             here — where it previously lost its brackets and rendered "1" against
             the server-rendered "(1)". */
          el.textContent = isDrawerCount ? `(${count})` : count;
        }

        /* Keep the screen-reader label in step with the visible number.
           Liquid renders "<span class="tc-sr-only">N items</span>" server-side,
           but AJAX adds only rewrote the aria-hidden numeric — leaving assistive
           tech announcing "0 items" no matter how many were in the cart. */
        const srTarget = el.querySelector('.tc-sr-only');
        if (srTarget) {
          srTarget.textContent = count === 1 ? '1 item' : `${count} items`;
        }

        /* sections/header.liquid stamps `tc-hidden` on the badge when the cart is
           empty, and base.css defines `.tc-hidden { display: none !important }`.
           An inline style cannot beat !important, so after the first add the badge
           had the right number, ran its bounce animation, and stayed invisible
           until the next page load. Toggle the class, not just the inline style. */
        el.classList.toggle('tc-hidden', count === 0);
        el.style.display = count > 0 ? '' : 'none';

        /* Trigger bounce when count grows (covers first-add and qty increase).
           Skip for the drawer-internal counter so the bounce only fires on
           the header badge. Respects prefers-reduced-motion via CSS. */
        if (count > previousCount && !el.hasAttribute('data-cart-drawer-count')) {
          el.classList.remove('is-bumped');
          /* Force reflow so animation restarts on rapid successive adds */
          void el.offsetWidth;
          el.classList.add('is-bumped');
        }
      });
    },

    /* The last cart rendered — empty or not. view_cart, the upsell and
       TheColor.Cart.current() read it instead of issuing their own GET. */
    _lastCart: null,

    /**
     * Pending discount code from the cookie — used only by the popup /
     * announcement pre-apply logic and as a fallback for the code row. All
     * MONEY maths below reads the cart payload instead: on this store
     * Shopify applies the code AT CART LEVEL (cart.total_price is already
     * discounted, cart.total_discount holds the real deduction), so
     * estimating from a percentage double-discounted.
     */
    getPendingDiscount() {
      const m = document.cookie.match(/(?:^|; )discount_code=([^;]+)/);
      return m ? { code: decodeURIComponent(m[1]).toUpperCase() } : null;
    },

    appliedDiscount(cart) {
      if (!cart || !cart.discount_codes) return null;
      const dc = cart.discount_codes.find((x) => x.applicable);
      if (!dc) return null;
      return { code: dc.code.toUpperCase(), amount: cart.total_discount || 0 };
    },

    /* Owner-listed code lists (Theme settings, exposed on window.theme by
       layout/theme.liquid): codes that mean free delivery, and codes only
       past customers can use. Upper-cased, blanks dropped. */
    codeList(key) {
      const t = window.theme || {};
      return (Array.isArray(t[key]) ? t[key] : [])
        .map((c) => String(c || '').trim().toUpperCase()).filter(Boolean);
    },

    /**
     * What one code on this cart means for the shopper:
     *   'applied'       — applicable now, money already off the total;
     *   'free-shipping' — a listed delivery code: /cart.js reports shipping
     *                     discounts as not applicable (they are priced at
     *                     checkout), so never call it invalid;
     *   'account-only'  — a listed returning-customer code (e.g. LOYAL10):
     *                     only checkout can tell who the shopper is;
     *   'unusable'      — anything else that does not apply to this basket.
     */
    welcomeCodes: ['WELCOME15', 'FIRST15'],

    codeState(code, cart) {
      const c = String(code || '').toUpperCase();
      const hit = ((cart && cart.discount_codes) || []).find((x) => String(x.code || '').toUpperCase() === c);
      if (hit && hit.applicable) return 'applied';
      if (this.codeList('freeShippingCodes').indexOf(c) > -1) return 'free-shipping';
      if (this.codeList('accountOnlyCodes').indexOf(c) > -1) return 'account-only';
      /* Welcome codes (18 Oct 2026, evening): WELCOME15 and FIRST15 now work
         for any customer, once each, so the cart reports them as applicable
         whenever the basket qualifies. When it doesn't (only Subscribe & save
         items) the row explains why instead of calling a live code unusable.
         FIRST15 on the website (19 Oct 2026): neither code has a minimum.
         FIRST15 is the code the site names. WELCOME15 (honoured for baskets
         that already carry it) that doesn't apply to one-off items isn't
         working (e.g. once it's switched off), so it reads as unusable and
         the basket's join row offers FIRST15 instead. */
      if (this.welcomeCodes.indexOf(c) > -1) {
        const oneOff = ((cart && cart.items) || []).some((i) => i && !i.selling_plan_allocation && !i.gift_card);
        return (c === 'WELCOME15' && hit && oneOff) ? 'unusable' : 'account-only';
      }
      return 'unusable';
    },

    /* The code the row talks about: an applied one first, else the first
       code on the cart, else the pending cookie. */
    currentCode(cart) {
      const d = this.appliedDiscount(cart);
      if (d) return d.code;
      const any = ((cart && cart.discount_codes) || []).find((x) => x && x.code);
      if (any) return String(any.code).toUpperCase();
      const pending = this.getPendingDiscount();
      return pending ? pending.code : null;
    },

    /* A listed free-delivery code on the cart (any state) or pending. */
    freeShippingCodeOn(cart) {
      const list = this.codeList('freeShippingCodes');
      if (!list.length) return null;
      const codes = ((cart && cart.discount_codes) || []).map((x) => String(x.code || '').toUpperCase());
      const pending = this.getPendingDiscount();
      if (pending) codes.push(pending.code);
      return codes.find((c) => list.indexOf(c) > -1) || null;
    },

    /**
     * The "£x more for free delivery" figure (19 Oct 2026, gap fix). The gap
     * (threshold - total_price) is in money AFTER codes, but the shopper
     * closes it by adding one-off items at SHELF prices, and a code takes its
     * share of those too. So the figure is the gap divided by what an added
     * one-off item keeps after the codes: x = ceil(gap / keep).
     *
     * keep comes from the discount applications themselves, never from the
     * basket's average. FIRST15 / WELCOME15 / COLOR10 are cart-level 15% /
     * 10% codes that skip Subscribe & save lines (read from /cart.js, 19 Oct),
     * so the old total_discount / original_total_price rate was diluted by
     * the subscription lines: C + FIRST15 (£28.82) said "add £1.25", which
     * only adds £1.06 after the 15%; the real figure is £1.39.
     *   - percentage: its own rate, as if every added one-off item qualifies
     *     (conservative for a code limited to some products, or to
     *     subscriptions: the figure may overshoot, never fall short);
     *   - anything else (fixed amounts; none on the store today): its share
     *     of the whole basket, as before.
     * Several applications stack (product discounts first, then the order
     * ones), so their keeps multiply. Whole numbers in parts per million, so
     * snippets/subscription-delivery.liquid (part 'gap', the first paint)
     * gets the same pence. x is the smallest whole penny with
     * total + x * keep >= threshold, so adding exactly x of a qualifying
     * one-off item always reaches it; Shopify drops each discount's
     * part-penny, which can make the bare minimum a penny or two less.
     * Also used by assets/product.js and assets/tc-recs.js (TheColor.CartUI).
     *
     * Response shapes (read 19 Oct 2026): GET /cart.js (and /cart/update.js
     * with an empty body) lists cart_level_discount_applications flat, as
     * the applications themselves. /cart/change.js and /cart/update.js
     * (quantity, selling plan, discount) wrap each one as an allocation,
     * {amount, discount_application: {...}}; every basket change, code typed
     * in the drawer, Subscribe & save switch and recs swap renders from
     * those. Both are unwrapped here, or the wrapped ones would fall back to
     * the basket average (F1 + FIRST15: £1.25 instead of £1.39).
     */
    freeKeep(cart) {
      const O = Number(cart && cart.original_total_price) || 0;
      const apps = [];
      const add = (x) => {
        const a = (x && x.discount_application) ? x.discount_application : x;
        if (a && (a.value_type || a.type || a.title)) apps.push(a);
      };
      ((cart && cart.cart_level_discount_applications) || []).forEach(add);
      ((cart && cart.items) || []).forEach((i) => {
        ((i && i.line_level_discount_allocations) || []).forEach(add);
      });
      const seen = {};
      let keep = 1000000;
      let any = false;
      const take = (cut) => {
        cut = Math.min(1000000, cut);
        if (!(cut > 0)) return;
        any = true;
        keep = Math.floor((1000000 - cut) * keep / 1000000);
      };
      apps.forEach((a) => {
        const k = (a.type || '') + ':' + (a.title || '');
        if (seen[k]) return;
        seen[k] = 1;
        if (a.value_type === 'percentage') take(Math.round(Number(a.value) * 10000) || 0);
        else if (O > 0) take(Math.ceil((Number(a.total_allocated_amount) || 0) * 1000000 / O));
      });
      if (!any && (cart && cart.total_discount) > 0 && O > 0) take(Math.ceil(cart.total_discount * 1000000 / O));
      return keep;
    },

    freeShelf(cart, gap) {
      if (!(gap > 0)) return 0;
      const keep = this.freeKeep(cart);
      return (keep > 0 && keep < 1000000) ? Math.ceil(gap * 1000000 / keep) : gap;
    },

    /**
     * Discount-aware shipping bar, driven by REAL cart numbers. States:
     *   1. total >= threshold                    -> free delivery locked in
     *   1b. a listed free-delivery code is on     -> free at checkout
     *   2. code took it below threshold (amber)  -> "add £X to get it back"
     *   3. plain below threshold                 -> "spend £X more"
     * The gap is threshold - total_price (total_price already includes the
     * applied code, as checkout judges it); X is that gap in shelf money,
     * freeShelf() above. The Liquid first paint (cart-drawer, cart-main,
     * subscription-delivery part 'gap') prints the same sentence and figure
     * for every basket with a code (19 Oct 2026).
     */
    updateShippingBar(cart) {
      if (typeof cart === 'number') { cart = { total_price: cart, original_total_price: cart, total_discount: 0, discount_codes: [] }; }
      const T = (window.theme && window.theme.freeShippingThreshold) || config.freeShippingThreshold;
      const total = cart.total_price;
      const d = this.appliedDiscount(cart);
      const freeCode = total < T ? this.freeShippingCodeOn(cart) : null;
      /* Subscribe & save (13 Oct 2026; 19 Oct): see subsInfo(). 'sub' and
         'mixed-free' ship free now and on every later delivery; 'mixed'
         gets the normal bar for the one-off items, which need the
         threshold, plus "Your subscription ships free every time." */
      const subsInfo = this.subsInfo(cart, T);
      const subs = subsInfo.state;
      const subsFree = subs === 'sub' || subs === 'mixed-free';
      const mixed = subs === 'mixed';
      const oneOff = mixed ? SUBS_COPY.mixedOneOff : '';
      const tail = mixed ? SUBS_COPY.mixedTail : '';
      /* Inner Circle (pipeline audit, 13 Oct 2026): the Liquid puts
         data-inner-circle-min (pence) on the bar for customers tagged
         'Rivo VIP Tier: Inner Circle'. Checkout's automatic "Inner Circle
         free delivery" discount then makes standard delivery free on
         one-off orders from that amount (not on subscriptions). */
      const icEl = document.querySelector('[data-inner-circle-min]');
      const icMin = icEl ? (parseInt(icEl.getAttribute('data-inner-circle-min'), 10) || 0) : 0;
      const icFree = icMin > 0 && !subs && total >= icMin && total < T;
      document.querySelectorAll(selectors.shippingBar).forEach((bar) => {
        const progress = bar.querySelector(selectors.shippingProgress);
        const message = bar.querySelector(selectors.shippingMessage);
        if (!progress || !message) return;
        if (!message.hasAttribute('aria-live')) message.setAttribute('aria-live', 'polite');
        const setMsg = (parts, success, warn) => {
          message.textContent = '';
          parts.forEach(([txt, strong]) => {
            if (strong) {
              const el = document.createElement('strong');
              el.textContent = txt;
              message.appendChild(el);
            } else {
              message.appendChild(document.createTextNode(txt));
            }
          });
          /* The success tick is drawn by CSS (::before on this class), so it
             survives every repaint — the old inline icon vanished on the
             first JS render. */
          message.classList.toggle('tc-cart-drawer__shipping-text--success', success);
          message.classList.toggle('tc-cart-main__shipping-msg--success', success && message.classList.contains('tc-cart-main__shipping-msg'));
          bar.classList.toggle('tc-shipping-bar--complete', success);
          bar.classList.toggle('tc-shipping--warn', warn);
        };
        progress.style.width = (freeCode || subsFree || icFree ? 100 : Math.min((total / T) * 100, 100)) + '%';
        /* The gap to the threshold is measured in DISCOUNTED money, but the
           shopper closes it by adding items at SHELF prices — a 20% code
           means every £1 they add only moves the bar 80p. Quote the SHELF
           figure so "add £X more" is literally actionable. 19 Oct 2026: at
           the rate the code takes off an added one-off item (freeShelf),
           not the basket's average, which Subscribe & save lines diluted. */
        const need = this.freeShelf(cart, T - total);
        if (subsFree) {
          setMsg([[subs === 'sub' ? SUBS_COPY.barSub : SUBS_COPY.barMixedFree, false]], true, false);
        } else if (total >= T) {
          /* 19 Oct 2026: no exclamation mark (Paul Mitchell wording rule);
             the same words as locales cart.general.free_shipping_success. */
          setMsg(d
            ? [["You've unlocked free delivery — with " + d.code + ' applied', true]]
            : [["You've unlocked free delivery.", true]], true, false);
        } else if (icFree) {
          setMsg([['Inner Circle: free UK standard delivery on this order', false]], true, false);
        } else if (freeCode) {
          setMsg([['Free delivery with ', false], [freeCode, true], [' — applied at checkout', false]], true, false);
        } else if (d && cart.original_total_price >= T) {
          setMsg([
            ['With ' + d.code + ' applied, add ', false],
            [this.formatMoney(need), true],
            [' more for free delivery' + oneOff + tail, false],
          ], false, true);
        } else {
          setMsg([['Spend ', false], [this.formatMoney(need), true],
                  [d ? ' more for free delivery' + oneOff + ' (' + d.code + ' applied)' + tail : ' more for free delivery' + oneOff + tail, false]],
                 false, false);
        }
        /* The old "check out separately" note (13–19 Oct) is no longer
           printed; hide it on a page cached from before the change. */
        bar.querySelectorAll('[data-subs-note]').forEach((n) => { n.hidden = true; });
      });
      /* The delivery caption agrees with the bar: once delivery is free it
         says so, instead of "Delivery calculated at checkout" right under
         "You've unlocked free delivery". */
      document.querySelectorAll('[data-shipping-note]').forEach((n) => {
        const def = n.getAttribute('data-default') || n.textContent;
        if (!n.hasAttribute('data-default')) n.setAttribute('data-default', def);
        n.textContent = (subsFree || total >= T || freeCode || icFree) ? 'Free standard delivery' : def;
      });
      this.updateDiscountRow(cart);
    },

    /**
     * Subscribe & save delivery (owner, 13 Oct 2026): 15% off and free UK
     * standard delivery on every subscription delivery, the first included.
     * Since 19 Oct 2026 a delivery profile makes that true in every basket
     * (read at checkout: docs/plan-2026-10-11/sns-free-delivery-2026-10-13/
     * after-profile-2026-10-19/): subscriptions only, with one-off items, and
     * on different schedules. One-off items still follow the normal rule,
     * judged on the WHOLE basket total after codes (subscription lines
     * included): free at the threshold, else standard delivery on the
     * one-off parcel only. (13–19 Oct a mixed or split basket paid £3.95 on
     * later deliveries; that rule and its note are gone.)
     *   null          no subscription line: the normal bar
     *   'sub'         subscription lines only (any total, any schedules):
     *                 free now and every delivery
     *   'mixed-free'  one-off items too, basket total at the threshold or
     *                 more: free now and every subscription delivery
     *   'mixed'       one-off items too, basket total under the threshold:
     *                 "Spend £x more for free delivery on your one-off items.
     *                 Your subscription ships free every time."
     * Lines that don't ship (gift cards) don't make a basket mixed. Mirrors
     * snippets/subscription-delivery.liquid (the first paint) and
     * assets/tc-recs.js subscriptionOnly() (= 'sub': no free-delivery closer).
     */
    subsInfo(cart, T) {
      let has = false;
      let other = false;
      ((cart && cart.items) || []).forEach((i) => {
        if (!i) return;
        if (i.selling_plan_allocation) has = true;
        else if (i.requires_shipping !== false) other = true;
      });
      const total = Number(cart && cart.total_price) || 0;
      let state = null;
      if (has) {
        if (!other) state = 'sub';
        else state = (T > 0 && total >= T) ? 'mixed-free' : 'mixed';
      }
      return { state, total, T };
    },

    subsState(cart, T) {
      return this.subsInfo(cart, T).state;
    },

    /**
     * The code row above the subtotal — real numbers straight from the cart,
     * and honest about codes that only checkout can price. Every state has a
     * Remove control, so an expired code from an old email (which Shopify
     * keeps on the cart and in the cookie) can't linger.
     */
    updateDiscountRow(cart) {
      const code = cart && cart.item_count ? this.currentCode(cart) : null;
      const state = code ? this.codeState(code, cart) : null;
      const applied = state === 'applied' ? this.appliedDiscount(cart) : null;
      /* Rebuild only when what the row says changes: it is a polite live
         region, so repainting it on every quantity tap re-announced the whole
         sentence each time (and dropped focus from its Remove control). */
      const rowKey = code ? code + '|' + state + '|' + (applied ? applied.amount : '') : '';
      document.querySelectorAll('[data-discount-row]').forEach((row) => {
        if (!row.hasAttribute('aria-live')) row.setAttribute('aria-live', 'polite');
        if (!code) { row.hidden = true; row.textContent = ''; row.removeAttribute('data-row-key'); return; }
        if (!row.hidden && row.getAttribute('data-row-key') === rowKey && row.firstChild) return;
        row.hidden = false;
        row.textContent = '';
        row.setAttribute('data-row-key', rowKey);
        row.setAttribute('data-state', state);
        const chip = document.createElement('span');
        chip.className = 'tc-discount-chip';
        chip.textContent = code;
        row.appendChild(chip);
        const txt = document.createElement('span');
        txt.className = 'tc-discount-row__text';
        if (state === 'applied') {
          const d = applied;
          txt.textContent = d && d.amount > 0
            ? ' applied: −' + this.formatMoney(d.amount)
            : ' applied';
        } else if (state === 'free-shipping') {
          txt.textContent = ' will be applied at checkout';
        } else if (state === 'account-only' && this.welcomeCodes.indexOf(String(code).toUpperCase()) > -1) {
          const oneOff = ((cart && cart.items) || []).some((i) => i && !i.selling_plan_allocation && !i.gift_card);
          /* No £20 minimum (19 Oct 2026), so the only reason it isn't applied
             yet is a basket of Subscribe & save items. */
          txt.textContent = !oneOff
            ? ' takes 15% off one-off items. Subscribe & save already has its own 15%.'
            : ' takes 15% off at checkout.';
        } else if (state === 'account-only') {
          /* The two long states read as a sentence on their own line under
             "CODE · Remove" (CSS: .tc-discount-row[data-state] …__text). */
          txt.appendChild(document.createTextNode('For returning customers: enter it at checkout after your email, or '));
          const a = document.createElement('a');
          a.href = row.getAttribute('data-login-url') || '/account/login';
          a.textContent = 'sign in';
          txt.appendChild(a);
          txt.appendChild(document.createTextNode(' first.'));
        } else {
          txt.textContent = 'Can’t be used on this basket.';
        }
        row.appendChild(txt);
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'tc-discount-row__remove';
        rm.setAttribute('data-discount-remove', '');
        rm.setAttribute('data-code', code);
        rm.setAttribute('aria-label', 'Remove code ' + code);
        rm.textContent = 'Remove';
        row.appendChild(rm);
      });
    },

    /**
     * Same-day dispatch countdown — the store's existing cut-off promise,
     * made visible at the point of decision. The clock (cut-off, bank
     * holidays, closed dates, holiday pause) is TheColor.Dispatch; the
     * wording is unchanged. The icon is CSS (no emoji).
     */
    initDispatchCountdown() {
      const render = () => {
        /* Re-query every tick: section swaps on /cart replace the node, and a
           captured reference would write into detached DOM forever. */
        const els = document.querySelectorAll('[data-dispatch-countdown]');
        if (!els.length) return;
        const st = dispatchStatus();
        let text = '';
        let sameDay = false;
        if (st.paused) {
          /* Holiday dispatch pause (Theme settings → Delivery): no countdown —
             the announcement bar carries the holiday message. Empty text hides
             the line (.tc-dispatch-countdown:empty). */
          text = '';
        } else if (st.sameDay) {
          const mins = Math.max(1, st.minsLeft);
          const h = Math.floor(mins / 60), m2 = mins % 60;
          text = 'Order within ' + (h > 0 ? h + 'h ' : '') + m2 + 'm for same-day dispatch';
          sameDay = true;
        } else {
          text = 'Order now — dispatched next working day';
        }
        els.forEach((el) => {
          el.textContent = '';
          if (!text) return;
          /* Same-day strings sit in .tc-sameday (B01 contract: hidden while
             html.tc-dispatch-paused). */
          const span = document.createElement('span');
          if (sameDay) span.className = 'tc-sameday';
          span.textContent = text;
          el.appendChild(span);
        });
      };
      render();
      this._countdownTick = render;
      setInterval(render, 60000);
    },

    /**
     * Update subtotal
     */
    updateSubtotal(totalPrice) {
      document.querySelectorAll(selectors.cartSubtotal).forEach((el) => {
        el.textContent = this.formatMoney(totalPrice);
      });
    },

    /**
     * Show/hide empty state, items list and footer.
     * Both [data-cart-items] and [data-cart-empty] are always in the DOM
     * (see snippets/cart-drawer.liquid) so these selectors always resolve.
     */
    updateEmptyState(itemCount) {
      const empty = document.querySelector(selectors.cartEmpty);   // [data-cart-empty]
      const items = document.querySelector(selectors.cartItems);   // [data-cart-items]
      const footer = document.querySelector(selectors.cartFooter); // [data-cart-footer]

      if (empty)  empty.style.display  = itemCount === 0 ? '' : 'none';
      if (items)  items.style.display  = itemCount > 0  ? '' : 'none';
      if (footer) footer.style.display = itemCount > 0  ? '' : 'none';

      /* An empty drawer doesn't nag "Spend £30.00 more for free delivery"
         beside "Your cart is empty", and the gift-message row only makes
         sense with something to send. */
      document.querySelectorAll('[data-cart-drawer] [data-cart-shipping-bar], [data-cart-drawer] [data-cart-note]').forEach((el) => {
        el.style.display = itemCount > 0 ? '' : 'none';
      });

      /* The upsell only re-evaluated itself on 'cart:open', so removing the last
         line left "Complete Your Routine" on screen — with a stale product card —
         directly beside the "your cart is empty" message. */
      if (itemCount === 0) {
        document.querySelectorAll('[data-cart-upsell]').forEach((upsell) => {
          upsell.style.display = 'none';
          const cards = upsell.querySelector('[data-cart-upsell-products]');
          if (cards) cards.innerHTML = '';
        });
      }
    },

    /**
     * Render all cart line items
     */
    /* The markup below MUST mirror the Liquid item template in
       snippets/cart-drawer.liquid. CartActions.init() re-renders the drawer on every
       page load, so this template — not the Liquid one — is what shoppers actually
       see. It previously emitted __quantity-btn / __quantity-input / __remove /
       __item-controls, none of which have a CSS rule, so the quantity controls
       rendered as bare 12px SVGs and the quantity box used browser-default styling. */
    renderItems(cart) {
      const container = document.querySelector(selectors.cartItems);
      if (!container) return;

      /* The innerHTML write below destroys whatever the shopper is focused on.
         For a keyboard user mid-way through "+ + +" that dropped focus to <body>,
         and the trap only intercepts Tab at the dialog edges — so the next Tab
         landed in the page BEHIND the aria-modal drawer. Capture what held focus
         (by stable data-key, falling back to data-line) and restore it after the
         rebuild. Pointer users are unaffected: focus is only restored when the
         destroyed element actually held it. */
      let refocus = null;
      const active = document.activeElement;
      if (active && container.contains(active)) {
        const row = active.closest('[data-key]');
        refocus = {
          action: active.getAttribute('data-action'),
          isQtyInput: active.hasAttribute('data-quantity-input'),
          key: row ? row.getAttribute('data-key') : null,
          line: active.getAttribute('data-line'),
        };
      }

      if (cart.items.length === 0) {
        container.innerHTML = '';
        /* Removing the last line via keyboard would otherwise strand focus on
           <body>; the close button is the only actionable control left. */
        if (refocus) {
          const closeBtn = document.querySelector(selectors.cartDrawerClose);
          if (closeBtn) closeBtn.focus();
        }
        return;
      }

      container.innerHTML = cart.items
        .map(
          (item, index) => `
        <li class="tc-cart-drawer__item" data-cart-item data-line="${index + 1}" data-line-index="${index + 1}" data-key="${this.escapeAttr(item.key || '')}" data-variant-id="${item.variant_id}" data-cart-item-id="${item.product_id}">
          <a href="${this.escapeAttr(item.url)}" class="tc-cart-drawer__item-image" aria-hidden="true" tabindex="-1">
            ${
              (item.featured_image && item.featured_image.url)
                ? `<img src="${this.escapeAttr(getSizedImageUrl(item.featured_image.url, 160))}" srcset="${this.escapeAttr(getSizedImageUrl(item.featured_image.url, 160))} 1x, ${this.escapeAttr(getSizedImageUrl(item.featured_image.url, 240))} 2x" alt="${this.escapeAttr((item.featured_image.alt || item.product_title))}" width="80" height="80" loading="lazy">`
                : (item.image
                  ? `<img src="${this.escapeAttr(getSizedImageUrl(item.image, 160))}" alt="${this.escapeAttr(item.product_title)}" width="80" height="80" loading="lazy">`
                  : '<div class="tc-cart-drawer__item-placeholder"></div>')
            }
          </a>
          <div class="tc-cart-drawer__item-details">
            <div class="tc-cart-drawer__item-header">
              <a href="${this.escapeAttr(item.url)}" class="tc-cart-drawer__item-title" title="${this.escapeAttr(item.product_title)}">${this.escapeHtml(this.lineName(item))}</a>
              <button type="button" class="tc-cart-drawer__item-remove" data-action="remove-item" data-line="${index + 1}" aria-label="Remove ${this.escapeAttr(item.product_title)} from cart">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            ${item.variant_title ? `<p class="tc-cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>` : ''}
            ${this.planLine(item)}
            <div class="tc-cart-drawer__item-footer">
              <div class="tc-cart-drawer__quantity">
                <button type="button" class="tc-cart-drawer__qty-btn" data-action="decrease-quantity" data-line="${index + 1}" aria-label="Decrease quantity of ${this.escapeAttr(item.product_title)}">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                </button>
                <input
                  type="number"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  class="tc-cart-drawer__qty-input"
                  data-quantity-input
                  data-line="${index + 1}"
                  value="${item.quantity}"
                  min="0"
                  max="99"
                  aria-label="Quantity of ${this.escapeAttr(item.product_title)}"
                >
                <button type="button" class="tc-cart-drawer__qty-btn" data-action="increase-quantity" data-line="${index + 1}" aria-label="Increase quantity of ${this.escapeAttr(item.product_title)}">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                </button>
              </div>
              <div class="tc-cart-drawer__item-price">${
                /* Mirror the Liquid branch: show the struck-through original when
                   the line is discounted. Omitting it here meant the saving was
                   invisible on every AJAX render — which, because init() re-renders
                   on every page load, is effectively always. On a catalogue built
                   around bundles and duos that hides the entire discount story at
                   the moment of decision. */
                item.original_line_price !== item.final_line_price
                  ? `<span class="tc-price__sale">${this.formatMoney(item.final_line_price)}</span>` +
                    `<span class="tc-price__compare">${this.formatMoney(item.original_line_price)}</span>`
                  : `<span>${this.formatMoney(item.final_line_price)}</span>`
              }</div>
            </div>
            <div class="tc-cart-sns" data-line-sns data-key="${this.escapeAttr(item.key || '')}" data-variant-id="${this.escapeAttr(String(item.variant_id || ''))}" hidden></div>
            <p class="tc-cart-line-upgrade" data-line-upgrade data-key="${this.escapeAttr(item.key || '')}" hidden></p>
          </div>
        </li>
      `
        )
        .join('');

      // Bind new event listeners
      this.bindItemListeners();

      /* Restore keyboard focus destroyed by the innerHTML write above. */
      if (refocus) {
        let row = refocus.key
          ? container.querySelector('[data-key="' + refocus.key + '"]')
          : null;
        if (!row && refocus.line) {
          row = container.querySelector('[data-cart-item][data-line="' + refocus.line + '"]');
        }
        let target = null;
        if (row) {
          target = refocus.isQtyInput
            ? row.querySelector('[data-quantity-input]')
            : (refocus.action ? row.querySelector('[data-action="' + refocus.action + '"]') : null);
          if (!target) target = row.querySelector('[data-quantity-input]');
        }
        if (!target) target = document.querySelector(selectors.cartDrawerClose);
        if (target) target.focus();
      }

      /* Takeover D1: the "Switch to Subscribe & save" rows (async: the
         variant's plans come from /products/<handle>.js). */
      SnsRows.fill(cart);
    },

    escapeHtml(text) {
      return window.TheColor.utils.escapeHtml(text);
    },

    /* Line name without the vendor prefix ("Paul Mitchell Tea Tree Special
       Shampoo" → "Tea Tree Special Shampoo"), as /cart and the upsell cards
       already show it. The full name stays in the link's title. */
    lineName(item) {
      const t = (item && item.product_title) || '';
      const vp = item && item.vendor ? item.vendor + ' ' : '';
      return vp && t.toLowerCase().indexOf(vp.toLowerCase()) === 0 && t.length > vp.length ? t.slice(vp.length) : t;
    },

    /* Subscribe & save line under the size, for a line bought on a selling
       plan; nothing for a one-off line (13 Oct 2026, owner: "clear
       everywhere including the cart"):
         "Subscribe & save · every 4 weeks · 15% off · free delivery every time"
       The frequency and the percentage come from the plan itself (its
       option "Deliver every 4 weeks" and its percentage price adjustment),
       so they follow any change made in the Subscriptions app; the plan's
       own name is the fallback. "free delivery every time" on every
       subscription line: since 19 Oct every subscription delivery ships
       free in any basket. The line price on the right is already the price
       per delivery. Mirrors snippets/subscription-delivery.liquid (cart
       drawer and /cart). */
    planLine(item) {
      const alloc = item && item.selling_plan_allocation;
      const plan = alloc && alloc.selling_plan;
      if (!plan || !plan.name) return '';
      const free = `<span class="tc-cart-drawer__item-plan-free"> ·\u00a0${this.escapeHtml(SUBS_COPY.lineFree)}</span>`;
      return `<p class="tc-cart-drawer__item-plan" data-selling-plan="${this.escapeAttr(String(plan.id || ''))}">` +
        `<span class="tc-cart-drawer__item-plan-name">${this.escapeHtml(this.planLabel(plan, alloc))}</span>${free}</p>`;
    },

    /* "Subscribe & save · every 4 weeks · 15% off" from the plan's data, or
       the plan's own name when no "every N weeks" can be read from it. */
    planLabel(plan, alloc) {
      const re = /every\s+\d+\s+(?:day|week|month|year)s?/i;
      const opt = plan.options && plan.options[0] && plan.options[0].value;
      const m = String(opt || '').match(re) || String(plan.name || '').match(re);
      if (!m) return String(plan.name || '');
      let pct = 0;
      const adj = (plan.price_adjustments || []).find((a) => a && a.value_type === 'percentage');
      if (adj && Number(adj.value) > 0) {
        pct = Math.round(Number(adj.value) * 10) / 10;
      } else if (alloc && Number(alloc.compare_at_price) > Number(alloc.price) && Number(alloc.price) > 0) {
        pct = Math.round((1 - Number(alloc.price) / Number(alloc.compare_at_price)) * 100);
      }
      /* No-break spaces keep "· every 4 weeks" and "· 15% off" whole when the
         line wraps on a phone. */
      return 'Subscribe & save ·\u00a0' + m[0].toLowerCase().replace(/\s+/g, '\u00a0') +
        (pct > 0 ? ' ·\u00a0' + pct + '%\u00a0off' : '');
    },

    escapeAttr(str) {
      return window.TheColor.utils.escapeAttr(str);
    },

    /**
     * Cheap identity signature for a cart payload. Includes item keys AND
     * quantities so any line-level change repaints, plus total_price and
     * total_discount so a discount code applying at unchanged quantities
     * (Discount.apply -> init) repaints too. Applicable codes are included
     * by name because a shipping-only code (e.g. FREEDELIVERY) changes NO
     * money field yet must still repaint the bar's "applied" state.
     */
    signature(cart) {
      /* Every code, applicable or not ("?" marks the pending ones): adding
         or removing a code the cart can't use yet changes no money field,
         but the code row must still repaint. The note is in too, so the
         drawer's gift message follows an edit made on /cart. */
      return cart.item_count + '|' + cart.total_price + '|' + (cart.total_discount || 0) + '|' +
        (cart.discount_codes || []).map(function (c) { return c.code + (c.applicable ? '' : '?'); }).join('+') + '|' +
        (cart.items || []).map(function (i) { return i.key + ':' + i.quantity; }).join(',') + '|' +
        (cart.note || '');
    },

    /**
     * Full cart update render
     */
    render(cart) {
      this._sig = this.signature(cart);
      this._lastCart = cart;
      this.updateCount(cart.item_count);
      this.updateSubtotal(cart.total_price);
      this.updateShippingBar(cart);
      this.updateEmptyState(cart.item_count);
      this.renderItems(cart);

      /* Keep the tracking cache current on EVERY render, not just when the drawer
         opens. begin_checkout reads this synchronously because the checkout click
         navigates away, and a shopper who goes straight to /cart never opens the
         drawer — so without this their begin_checkout would have no payload. */
      if (cart && cart.items && cart.items.length) {
        GtagEvents._lastViewCartPayload = cart;
      }

      /* Let the drawer upsell re-evaluate itself. It previously refreshed only on
         'cart:open', so after a removal it kept recommending against the old cart
         — including sizing suggestions off a line that was no longer there. */
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { itemCount: cart.item_count, cart: cart } })
      );
    },

    /**
     * Bind event listeners to dynamically rendered line items
     */
    bindItemListeners() {
      const container = document.querySelector(selectors.cartItems);
      if (!container || container._tcDelegated) return;
      container._tcDelegated = true;

      // Single delegated click handler for all cart item buttons
      container.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        e.preventDefault();
        const line = parseInt(btn.dataset.line, 10);
        const action = btn.dataset.action;

        /* Address the line by its stable key where possible — queued requests
           must not be resolved against an index that may have shifted. */
        const row = btn.closest('[data-key]');
        const key = row ? row.getAttribute('data-key') : null;

        if (action === 'remove-item') {
          CartActions.updateQuantity(line, 0, key);
        } else if (action === 'decrease-quantity') {
          const input = container.querySelector(
            `${selectors.lineItemQuantity}[data-line="${line}"]`
          );
          if (input) CartActions.updateQuantity(line, Math.max(0, parseInt(input.value, 10) - 1), key);
        } else if (action === 'increase-quantity') {
          const input = container.querySelector(
            `${selectors.lineItemQuantity}[data-line="${line}"]`
          );
          if (input) CartActions.updateQuantity(line, parseInt(input.value, 10) + 1, key);
        }
      });

      // Single delegated change handler for quantity inputs.
      // NO debounce: 'change' on a number input already fires on commit
      // (blur/Enter), and a 300ms timer opened a race where tapping Checkout
      // unloaded the page before the update fired — old quantity got bought.
      // The shared timer also let editing line B cancel line A's update.
      container.addEventListener('change', (e) => {
        const input = e.target.closest(selectors.lineItemQuantity);
        if (!input) return;
        const row = input.closest('[data-key]');
        const key = row ? row.getAttribute('data-key') : null;
        {
          const line = parseInt(input.dataset.line, 10);
          const newQty = Math.max(0, parseInt(input.value, 10) || 0);
          CartActions.updateQuantity(line, newQty, key);
        }
      });
    },
  };

  /* --------------------------------------------------------------------------
     6. Google Ads E-Commerce Events
     -------------------------------------------------------------------------- */

  const GtagEvents = {
    /**
     * Map cart items to GA4 items array format.
     *
     * Enriched 2026-05-21 with item_category (Shopify product_type) and
     * google_business_vertical to give Google Ads Smart Bidding richer
     * context for bid decisions. Higher signal density → better auction
     * win-rate forecasting.
     */
    mapItems(cartItems) {
      return cartItems.map(function (item, index) {
        return {
          /* Feed-aligned id (shopify_ZZ_{product}_{variant}) — matches the
             Google & YouTube channel's feed item ids for GA4/Ads joins. */
          item_id: 'shopify_ZZ_' + item.product_id + '_' + item.variant_id,
          item_name: item.product_title,
          item_brand: item.vendor,
          item_category: item.product_type || undefined,
          item_variant: item.variant_title || undefined,
          google_business_vertical: 'retail',
          price: item.final_price / 100,
          quantity: item.quantity,
          index: index,
        };
      });
    },

    /**
     * Fire add_to_cart for a successful add.
     *
     * Called from CartActions.addToCart, which is the single funnel every add
     * path routes through — PDP form, PDP sticky bar, collection quick-add,
     * duo CTA, the generic cart form and the drawer upsell. Previously the event
     * existed in exactly ONE of those (assets/product.js), so five of six add
     * surfaces reported nothing to Google Ads — including the drawer upsell,
     * which is now the store's main cross-sell.
     *
     * `added` is the /cart/add.js response, whose items carry the real line data.
     */
    addToCart(added, cart, listContext) {
      if (typeof gtag !== 'function' || !window.TheColor.utils.hasConsent()) return;
      /* Accepts three shapes: the /cart/add.js response ({items:[…]}), a plain
         array of line items (the quantity-increase path), or a single line item.
         Missing the array case wrapped it as [[item]] and produced a null value. */
      const items = Array.isArray(added) ? added
        : (added && added.items) ? added.items
        : (added ? [added] : []);
      if (!items.length) return;
      const value = items.reduce(function (sum, i) {
        return sum + ((i.final_price != null ? i.final_price : i.price) / 100) * (i.quantity || 1);
      }, 0);
      const payload = {
        currency: (cart && cart.currency) || 'GBP',
        value: value,
        items: this.withListContext(this.mapItems(items), listContext),
      };
      /* Recommendation attribution (TheColor.Recs): which strip the add came
         from — 'pdp_pairs' or 'cart_upsell' — so GA4 can report attach rate. */
      if (listContext && listContext.list) {
        payload.item_list_id = listContext.list;
        payload.item_list_name = listContext.list;
      }
      gtag('event', 'add_to_cart', payload);
    },

    /**
     * Decorate mapped items with an optional recommendation list context
     * ({list, index, reason, freeDelivery, saving}). No context → unchanged.
     */
    withListContext(mapped, ctx) {
      if (!ctx || !ctx.list) return mapped;
      return mapped.map(function (item) {
        const out = Object.assign({}, item, {
          item_list_id: ctx.list,
          item_list_name: ctx.list,
        });
        if (typeof ctx.index === 'number') out.index = ctx.index;
        if (ctx.reason) out.rec_reason = ctx.reason;
        if (ctx.freeDelivery) out.rec_free_delivery = ctx.freeDelivery;
        if (typeof ctx.saving === 'number') out.rec_saving = ctx.saving / 100;
        return out;
      });
    },

    /**
     * Fire begin_checkout event.
     *
     * `transport_type: 'beacon'` matters here: both checkout controls navigate
     * away synchronously (an <a href> in the drawer, a submit button on /cart),
     * so a normal XHR beacon is cancelled by the unload and the event is lost.
     */
    beginCheckout(cart) {
      if (typeof gtag !== 'function' || !cart || !cart.items.length || !window.TheColor.utils.hasConsent()) return;
      gtag('event', 'begin_checkout', {
        currency: cart.currency || 'GBP',
        value: cart.total_price / 100,
        items: this.mapItems(cart.items),
        transport_type: 'beacon',
      });
    },

    /**
     * Fire remove_from_cart event for items being removed or decreased
     */
    removeFromCart(item, removedQty, listContext) {
      if (typeof gtag !== 'function' || !window.TheColor.utils.hasConsent()) return;
      gtag('event', 'remove_from_cart', {
        currency: 'GBP',
        value: (item.final_price / 100) * removedQty,
        items: this.withListContext([{
          item_id: 'shopify_ZZ_' + item.product_id + '_' + item.variant_id,
          item_name: item.product_title,
          item_brand: item.vendor,
          item_category: item.product_type || undefined,
          item_variant: item.variant_title || undefined,
          google_business_vertical: 'retail',
          price: item.final_price / 100,
          quantity: removedQty,
        }], listContext),
      });
    },

    /**
     * Fire view_cart event.
     *
     * Caches the last cart payload so we can retroactively fire when the
     * visitor accepts consent after viewing the cart page — recovers the
     * view_cart signal that would otherwise be lost. Pattern mirrors the
     * fireViewItemEvent retro-fire in assets/product.js.
     */
    viewCart(cart) {
      if (cart && cart.items && cart.items.length) {
        this._lastViewCartPayload = cart;
      }
      if (typeof gtag !== 'function' || !cart || !cart.items.length || !window.TheColor.utils.hasConsent()) return;
      gtag('event', 'view_cart', {
        currency: 'GBP',
        value: cart.total_price / 100,
        items: this.mapItems(cart.items),
      });
    },

    /**
     * Re-fire view_cart with the most recent cart payload (called from the
     * tc:consent-granted listener below).
     */
    _replayViewCart() {
      var cart = this._lastViewCartPayload;
      if (!cart) return;
      if (typeof gtag !== 'function' || !window.TheColor.utils.hasConsent()) return;
      gtag('event', 'view_cart', {
        currency: 'GBP',
        value: cart.total_price / 100,
        items: this.mapItems(cart.items),
      });
    },
  };

  // Signal recovery: if consent is granted AFTER viewCart was called with
  // consent denied (the lost-signal case), replay the event now.
  document.addEventListener('tc:consent-granted', function () {
    if (window.TheColor && window.TheColor.GtagEvents &&
        typeof window.TheColor.GtagEvents._replayViewCart === 'function') {
      window.TheColor.GtagEvents._replayViewCart();
    }
  }, { once: true });

  /* --------------------------------------------------------------------------
     7. Cart Actions
     -------------------------------------------------------------------------- */

  /* Quantity of one variant across all its lines. */
  function qtyOfVariant(cart, variantId) {
    const id = Number(variantId);
    return ((cart && cart.items) || []).reduce(function (n, i) {
      return n + (Number(i.variant_id) === id ? i.quantity : 0);
    }, 0);
  }

  /* Did a /cart/update.js `updates` map land? Line keys ("123:abc…") must
     hold exactly the requested quantity (0 = gone); variant ids must hold
     at least it. Used to tell a timed-out request that succeeded from one
     that didn't. */
  function updatesLanded(cart, updates) {
    return Object.keys(updates || {}).every(function (k) {
      const want = Number(updates[k]) || 0;
      if (/^\d+$/.test(k)) return qtyOfVariant(cart, k) >= want;
      const line = ((cart && cart.items) || []).find(function (i) { return i.key === k; });
      return (line ? line.quantity : 0) === want;
    });
  }

  function clearDiscountCookie() {
    /* Shopify sets discount_code readable (not HttpOnly) on the shop's host;
       expire it on the host and the parent domain so the row can't revive a
       removed code. */
    const host = window.location.hostname;
    const kill = 'discount_code=; Max-Age=0; path=/; SameSite=Lax; Secure';
    document.cookie = kill;
    document.cookie = kill + '; domain=' + host;
    const parts = host.split('.');
    if (parts.length > 2) document.cookie = kill + '; domain=.' + parts.slice(-2).join('.');
  }

  /* Subscribe & save (S1): the selling plan chosen for a product form, or
     null for a one-off purchase. Shopify's Subscriptions app block writes a
     hidden input name="selling_plan" (empty value = one-off) and copies it
     into every /cart/add form in its section; a radio group named
     selling_plan counts only when checked. FormData reads the form's own
     controls plus any tied to it with form="…". `scope` is a fallback for a
     plan input rendered outside the form, used only when the form has no
     selling_plan control at all — the product page passes its own section;
     a grid of quick-add forms must not, or it could borrow another card's
     plan. */
  function sellingPlanFrom(form, scope) {
    let value = '';
    try {
      const own = form && form.elements && form.elements.namedItem('selling_plan');
      if (own) {
        const all = new FormData(form).getAll('selling_plan');
        value = all.find(function (v) { return typeof v === 'string' && v.trim() !== ''; }) || '';
      } else if (scope) {
        scope.querySelectorAll('input[name="selling_plan"], select[name="selling_plan"]').forEach(function (el) {
          if (value || el.disabled) return;
          if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
          if (el.value && el.value.trim() !== '') value = el.value;
        });
      }
    } catch (e) { value = ''; }
    const plan = parseInt(value, 10);
    return plan > 0 ? plan : null;
  }

  const CartActions = {
    /* Exposed for product.js and the sticky Add to Cart (sections/product-main). */
    sellingPlanFrom: sellingPlanFrom,

    /**
     * Add item to cart and open drawer. `sellingPlan` (optional, 5th) is the
     * Subscribe & save plan id; leave it out for a one-off add.
     */
    async addToCart(variantId, quantity = 1, properties = {}, listContext = null, sellingPlan = null) {
      /* The control behind this add, read NOW (before the queue): it is the
         drawer's focus-return target, and by the time the drawer opens the
         caller has usually disabled or removed it. */
      const opener = captureOpener();
      /* Queue rather than drop. The drawer upsell renders up to three "+ Add"
         cards and shoppers do click several in quick succession; the previous
         `if (isUpdating) return` silently discarded every click that arrived
         while a request was in flight, so the item was never added and no error
         was shown — a lost sale the shopper could not see. */
      const run = async () => {
        isUpdating = true;
        cartGeneration++;
        /* Pre-add state (the queue serialises us, so the render cache is the
           current cart) — lets a timed-out add be checked against the server. */
        const before = CartRenderer._lastCart;
        /* add_to_cart reports what THIS click added: /cart/add.js returns the
           line's new total, so a repeat add of the same variant used to
           report quantity 2 and double the value (sweep B8-16). */
        const asAdded = (lines) => (lines || []).map(function (i) {
          return Object.assign({}, i, { quantity: quantity });
        });
        try {
          const added = await CartAPI.add(variantId, quantity, properties, sellingPlan);
          const cart = await CartAPI.get();
          /* Reported only once the add has actually succeeded. product.js used to
             fire this BEFORE attempting the add, so sold-out 422s and network
             failures were all counted as adds — inflating the add_to_cart figure
             Smart Bidding optimises against, concentrated on exactly the sessions
             least likely to convert. */
          GtagEvents.addToCart(asAdded(added && added.items ? added.items : [added]), cart, listContext);
          CartRenderer.render(cart);
          CartDrawer.setNotice('');
          CartDrawer.open({ returnTo: opener });
          return cart;
        } catch (error) {
          /* A request that timed out may still have landed (slow mobile
             network). Ask the server before calling it a failure. */
          let recovered = null;
          if (error && error.isTimeout) {
            try { recovered = await CartAPI.get(); } catch (e) { /* offline */ }
            if (recovered && before && qtyOfVariant(recovered, variantId) - qtyOfVariant(before, variantId) >= quantity) {
              const line = recovered.items.find(function (i) { return Number(i.variant_id) === Number(variantId); });
              if (line) GtagEvents.addToCart(asAdded([line]), recovered, listContext);
              CartRenderer.render(recovered);
              CartDrawer.setNotice('');
              CartDrawer.open({ returnTo: opener });
              return recovered;
            }
          }
          /* A PARTIAL add: Shopify answers 422 ("Only 1 item was added to
             your cart due to availability.") having already added what it
             had. That is an add — the shopper must see it, analytics must
             count it — not a failure (QA Q5-06: the drawer stayed shut, the
             toast faded in 4 s and the added unit sent no add_to_cart). */
          if (!error.isTimeout && before) {
            try { recovered = await CartAPI.get(); } catch (e) { /* offline */ }
            const got = recovered ? qtyOfVariant(recovered, variantId) - qtyOfVariant(before, variantId) : 0;
            if (got > 0) {
              const line = recovered.items.find(function (i) { return Number(i.variant_id) === Number(variantId); });
              if (line) GtagEvents.addToCart([Object.assign({}, line, { quantity: got })], recovered, listContext);
              CartRenderer.render(recovered);
              const note = error.message || ('Only ' + got + ' could be added to your basket.');
              document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: note, partial: true, added: got } }));
              CartDrawer.setNotice(note);
              CartDrawer.open({ returnTo: opener });
              return recovered;
            }
          }
          // Surfaced to the user via the cart:error toast...
          document.dispatchEvent(
            new CustomEvent('cart:error', {
              detail: { message: error.message },
            })
          );

          /* A failed add does NOT mean an unchanged cart. Shopify returns an
             error status for a PARTIAL add ("Only 5 items were added... due to
             availability") having already applied those 5. Without this re-sync
             the badge and drawer kept showing the pre-add totals while the server
             held more — measured: server 5, UI 2. Best-effort, and it must not
             mask the original error. */
          try {
            if (!recovered) recovered = await CartAPI.get();
            CartRenderer.render(recovered);
          } catch (e) { /* offline — leave the UI as it stands */ }
          /* ...and rethrown so callers learn it failed. Swallowing it here meant
             the promise fulfilled on failure, so every caller ran its success
             branch: product.js and the sticky mobile bar both flashed "Added ✓"
             while the error toast said the opposite, and their .catch branches
             were unreachable dead code. */
          throw error;
        } finally {
          isUpdating = false;
        }
      };
      /* The queue must survive a failed add, so it continues from a settled
         promise, while the caller still receives the real outcome. */
      const result = addQueue.then(run, run);
      addQueue = result.catch(function () {});
      return result;
    },

    /**
     * Fire begin_checkout and navigate to checkout
     */
    beginCheckout() {
      /* Synchronous by design. This used to `await CartAPI.get()` first, but the
         checkout controls navigate immediately on click, so the page unloaded
         during that round trip and the event almost never sent — losing the last
         funnel step before Shopify's own checkout pixel takes over.
         GtagEvents caches the most recent cart from every render, so the payload
         is already in memory and needs no network call. */
      try {
        const cart = GtagEvents._lastViewCartPayload;
        if (cart) GtagEvents.beginCheckout(cart);
      } catch (e) {
        // Never block checkout on tracking failure
      }
    },

    /**
     * Update line item quantity (0 to remove).
     * `opts.sections` (e.g. 'cart-main') asks Shopify to return that
     * section's fresh HTML in the same round trip (cart.sections[…]).
     * Resolves with the new cart, or null when the change failed (the
     * cart:error toast has already told the shopper) — never rejects, so
     * the delegated drawer handlers need no catch.
     */
    async updateQuantity(line, quantity, key, opts = {}) {
      /* Queued, not dropped. The old `if (isUpdating) return` meant a remove or
         +/- tap made while ANY other cart request was in flight did nothing at
         all — no spinner, no toast, no change — and was never retried. Queueing
         is only safe because the operation is addressed by `key` where one is
         available; see CartAPI.change. */
      const run = async () => {
      isUpdating = true;
      cartGeneration++;

      // Capture current item data before update for remove_from_cart tracking
      /* Tracking deltas come from the render cache — a serial GET here
         doubled the perceived latency of every quantity tap. render()
         refreshes the cache on every mutation, and addQueue serialises us,
         so the cache IS the pre-mutation state. */
      let previousCart = CartRenderer._lastCart || (GtagEvents && GtagEvents._lastViewCartPayload) || null;

      // Optimistic UI: add loading state
      const drawer = document.querySelector(selectors.cartDrawer);
      if (drawer) drawer.classList.add('is-loading');

      const track = (cart) => {
        // Fire remove_from_cart if quantity decreased
        const prevIndex = key && previousCart
          ? previousCart.items.findIndex(function (i) { return i.key === key; })
          : line - 1;
        if (previousCart && prevIndex >= 0 && previousCart.items[prevIndex]) {
          const prevItem = previousCart.items[prevIndex];
          const newItem = cart.items.find(function (i) { return i.variant_id === prevItem.variant_id; });
          const newQty = newItem ? newItem.quantity : 0;
          if (newQty < prevItem.quantity) {
            GtagEvents.removeFromCart(prevItem, prevItem.quantity - newQty);
          } else if (newQty > prevItem.quantity && newItem) {
            /* The mirror case. Only the decrease was ever reported, so the drawer's
               "−" produced remove_from_cart while "+" produced nothing — a funnel
               biased towards removals by construction. The drawer "+" is a real
               AOV lever on this store, and every unit it added was invisible. */
            GtagEvents.addToCart(
              [Object.assign({}, newItem, { quantity: newQty - prevItem.quantity })],
              cart
            );
          }
        }
      };

      try {
        const cart = await CartAPI.change(key || line, quantity, opts.sections);
        track(cart);
        CartRenderer.render(cart);
        CartDrawer.setNotice('');
        return cart;
      } catch (error) {
        let recovered = null;
        /* Timed out: did it land anyway? Only checkable by line key. */
        if (error && error.isTimeout && key) {
          try { recovered = await CartAPI.get(); } catch (e) { /* offline */ }
          if (recovered && updatesLanded(recovered, { [key]: Math.max(0, quantity) })) {
            track(recovered);
            CartRenderer.render(recovered);
            return recovered;
          }
        }
        // Silent in production — surfaced to user via cart:error toast
        document.dispatchEvent(
          new CustomEvent('cart:error', {
            detail: { message: error.message },
          })
        );
        /* Refresh cart to restore correct state. This recovery GET can itself
           reject — the realistic cause of the failure above is a dropped
           connection, which takes this request down too. Unguarded, the throw
           escaped updateQuantity and became an unhandled rejection at the
           delegated click handlers, which attach no catch. */
        try {
          if (!recovered) recovered = await CartAPI.get();
          CartRenderer.render(recovered);
        } catch (e) {
          /* Leave the drawer as-is; the error toast has already been shown. */
        }
        return null;
      } finally {
        isUpdating = false;
        if (drawer) drawer.classList.remove('is-loading');
      }
      };
      /* Share the one queue with addToCart so mutations never interleave. */
      const result = addQueue.then(run, run);
      addQueue = result.catch(function () {});
      return result;
    },

    /**
     * Takeover (13 Oct 2026, D1): put one cart line on a Subscribe & save
     * plan, move it to another plan, or (planId null) back to one-off.
     * Shopify takes `selling_plan` only with the 1-based `line` (not the
     * key) and turns a missing quantity into 1, so inside the shared queue:
     * a fresh GET /cart.js, line = index(key) + 1, POST /cart/change.js
     * {line, quantity: <current>, selling_plan}, then verify that the
     * variant now carries that plan with at least the same quantity (a
     * merge into a line already on that plan is fine). Not verified: re-read
     * the cart (a slow request may have landed), else one retry by key, else
     * the cart:error toast and a re-render from the server's truth.
     * `opts.sections` asks for that section's HTML in the same round trip.
     * Resolves with the new cart, or null on failure; never rejects.
     */
    async setSellingPlan(key, planId, opts = {}) {
      const plan = parseInt(planId, 10) > 0 ? parseInt(planId, 10) : null;
      const FAIL = "We couldn't switch that item. Please try again.";
      const run = async () => {
        isUpdating = true;
        cartGeneration++;
        const drawer = document.querySelector(selectors.cartDrawer);
        if (drawer) drawer.classList.add('is-loading');
        let target = null;
        const landed = (c) => {
          if (!c || !c.items || !target) return false;
          let qty = 0;
          c.items.forEach((i) => {
            if (!i || i.variant_id !== target.variant_id) return;
            const a = i.selling_plan_allocation;
            const id = a ? Number((a.selling_plan && a.selling_plan.id) || a.selling_plan_id) : null;
            if (plan ? id === plan : !a) qty += i.quantity;
          });
          return qty >= target.quantity;
        };
        const post = async (body) => {
          if (opts.sections) body.sections = opts.sections;
          const response = await fetchWithTimeout('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          if (!response.ok) {
            let msg = FAIL;
            try { const err = await response.json(); msg = err.description || err.message || msg; } catch (e) {}
            throw new Error(msg);
          }
          return response.json();
        };
        try {
          const before = await CartAPI.get();
          const idx = ((before && before.items) || []).findIndex((i) => i.key === key);
          if (idx < 0) throw new Error(FAIL);
          target = before.items[idx];
          let cart = null;
          try {
            cart = await post({ line: idx + 1, quantity: target.quantity, selling_plan: plan });
          } catch (e) { cart = null; }
          if (!landed(cart)) {
            let now = null;
            try { now = await CartAPI.get(); } catch (e) { /* offline */ }
            if (landed(now)) {
              cart = now;
            } else {
              const still = now && now.items && now.items.some((i) => i.key === key);
              cart = still ? await post({ id: key, quantity: target.quantity, selling_plan: plan }) : null;
              if (!landed(cart)) throw new Error(FAIL);
            }
          }
          CartRenderer.render(cart);
          CartDrawer.setNotice('');
          return cart;
        } catch (error) {
          document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: FAIL } }));
          try { CartRenderer.render(await CartAPI.get()); } catch (e) { /* leave the UI as it stands */ }
          return null;
        } finally {
          isUpdating = false;
          if (drawer) drawer.classList.remove('is-loading');
        }
      };
      const result = addQueue.then(run, run);
      addQueue = result.catch(function () {});
      return result;
    },

    /**
     * Apply several line changes in ONE request (the recommendation "Swap"
     * card: two single lines → 0 and the duo/twin variant → n; the size
     * upgrade: one line → 0 and the bigger size → n). `updates` is keyed by
     * line-item KEY (existing lines) or variant id (new lines), as
     * /cart/update.js accepts. Fires remove_from_cart / add_to_cart for the
     * real diff (with the list context in `meta`), renders, and resolves with
     * the new cart. Same serial queue as every other mutation.
     */
    async applyUpdates(updates, meta = {}) {
      const run = async () => {
        isUpdating = true;
        cartGeneration++;
        const drawer = document.querySelector(selectors.cartDrawer);
        if (drawer) drawer.classList.add('is-loading');
        /* Pre-mutation state for the analytics diff: the render cache is
           the current cart (the queue serialises us), as in updateQuantity. */
        let before = CartRenderer._lastCart || (GtagEvents && GtagEvents._lastViewCartPayload) || null;
        const ctx = meta && meta.list ? meta : null;
        const finish = (cart) => {
          /* Quantity per variant before/after, one representative line each. */
          const tally = function (c) {
            const m = {};
            ((c && c.items) || []).forEach(function (i) {
              if (!m[i.variant_id]) m[i.variant_id] = { item: i, qty: 0 };
              m[i.variant_id].qty += i.quantity;
            });
            return m;
          };
          const was = tally(before);
          const now = tally(cart);
          Object.keys(was).forEach(function (vid) {
            const d = was[vid].qty - (now[vid] ? now[vid].qty : 0);
            if (d > 0) GtagEvents.removeFromCart(was[vid].item, d, ctx);
          });
          const added = [];
          Object.keys(now).forEach(function (vid) {
            const d = now[vid].qty - (was[vid] ? was[vid].qty : 0);
            if (d > 0) added.push(Object.assign({}, now[vid].item, { quantity: d }));
          });
          if (added.length) GtagEvents.addToCart(added, cart, ctx);
          CartRenderer.render(cart);
          return cart;
        };
        try {
          if (!before) before = await CartAPI.get();
          const response = await fetchWithTimeout('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates }),
          });
          if (!response.ok) {
            let msg = 'Failed to update cart';
            try { const err = await response.json(); msg = err.description || err.message || msg; } catch (e) {}
            throw new Error(msg);
          }
          return finish(await response.json());
        } catch (error) {
          let recovered = null;
          if (error && error.isTimeout && before) {
            try { recovered = await CartAPI.get(); } catch (e) { /* offline */ }
            if (recovered && updatesLanded(recovered, updates)) return finish(recovered);
          }
          document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: error.message } }));
          try {
            if (!recovered) recovered = await CartAPI.get();
            CartRenderer.render(recovered);
          } catch (e) { /* offline — leave the UI as it stands */ }
          throw error;
        } finally {
          isUpdating = false;
          if (drawer) drawer.classList.remove('is-loading');
        }
      };
      const result = addQueue.then(run, run);
      addQueue = result.catch(function () {});
      return result;
    },

    /**
     * Apply a typed discount code (drawer field). Uses the Cart AJAX
     * `discount` parameter (one round trip, returns discount_codes with
     * `applicable`), falling back to the /discount/ redirect on stores
     * without it. Outcome per CartRenderer.codeState: a code this basket
     * can't use is taken off again (and the previous working code kept),
     * while listed delivery and returning-customer codes stay on the cart
     * for checkout. Resolves { state, code, cart, kept }.
     */
    async applyDiscount(code) {
      code = String(code || '').replace(/\s+/g, '').toUpperCase();
      const run = async () => {
        isUpdating = true;
        cartGeneration++;
        try {
          const before = CartRenderer._lastCart || await CartAPI.get();
          /* Codes worth keeping if the new one fails: working ones, and the
             owner-listed delivery / returning-customer codes (not applicable
             in the cart by design). */
          const prev = ((before && before.discount_codes) || []).filter(function (x) {
            return x.applicable || CartRenderer.codeState(x.code, before) !== 'unusable';
          }).map(function (x) { return String(x.code).toUpperCase(); });
          /* `discount` REPLACES the cart's codes. A normal code still swaps
             out the previous one (switching to a better code must work), but
             the owner-listed delivery / returning-customer codes are never
             priced in the cart, so they can't clash there: carry them along,
             and when the NEW code is one of them, keep the working code too
             — typing a delivery code must not silently drop a 20% code.
             Checkout settles any combination rules. */
          const listed = function (c) { return CartRenderer.codeState(c, null) !== 'unusable'; };
          const newListed = listed(code);
          const carry = prev.filter(function (c) { return c !== code && (newListed || listed(c)); });
          let cart = null;
          try {
            cart = await CartAPI.update({ discount: [code].concat(carry).join(',') });
            /* A store without the `discount` parameter ignores it and returns
               the cart unchanged — treat "code not mentioned" as unsupported. */
            if (!cart || !Array.isArray(cart.discount_codes) ||
                !cart.discount_codes.some(function (x) { return String(x.code || '').toUpperCase() === code; })) cart = null;
          } catch (e) { cart = null; }
          if (!cart) {
            await fetchWithTimeout('/discount/' + encodeURIComponent(code), { credentials: 'same-origin', redirect: 'manual' }).catch(function () {});
            cart = await CartAPI.get();
          }
          const state = CartRenderer.codeState(code, cart);
          let kept = null;
          if (state === 'unusable') {
            /* A typo must never cost a working code: put the previous code
               back (or clear the dead one so it doesn't linger as pending). */
            kept = prev.filter(function (c) { return c !== code; }).join(',') || null;
            try {
              cart = await CartAPI.update({ discount: kept || '' });
            } catch (e) {
              if (kept) await fetchWithTimeout('/discount/' + encodeURIComponent(kept), { credentials: 'same-origin', redirect: 'manual' }).catch(function () {});
              try { cart = await CartAPI.get(); } catch (e2) { /* keep the last cart */ }
            }
            if (!kept) clearDiscountCookie();
          }
          CartRenderer.render(cart);
          return { state: state, code: code, cart: cart, kept: kept };
        } finally {
          isUpdating = false;
        }
      };
      const result = addQueue.then(run, run);
      addQueue = result.catch(function () {});
      return result;
    },

    /**
     * Remove a code from the cart (the row's Remove button). Only that code:
     * any other code on the cart stays (the row then shows the next one).
     * No code given = remove them all.
     */
    async removeDiscount(code) {
      const target = String(code || '').toUpperCase();
      const run = async () => {
        isUpdating = true;
        cartGeneration++;
        try {
          let cart;
          try {
            const before = target ? (CartRenderer._lastCart || await CartAPI.get()) : null;
            const rest = target
              ? ((before && before.discount_codes) || [])
                .map(function (x) { return String((x && x.code) || '').toUpperCase(); })
                .filter(function (c, i, a) { return c && c !== target && a.indexOf(c) === i; })
              : [];
            cart = await CartAPI.update({ discount: rest.join(',') });
          } catch (e) {
            document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: 'Couldn’t remove the code. Please try again.' } }));
            cart = null;
          }
          clearDiscountCookie();
          if (!cart || !Array.isArray(cart.discount_codes)) {
            try { cart = await CartAPI.get(); } catch (e) { cart = null; }
          }
          if (cart) CartRenderer.render(cart);
          return cart;
        } finally {
          isUpdating = false;
        }
      };
      const result = addQueue.then(run, run);
      addQueue = result.catch(function () {});
      return result;
    },

    /**
     * Save the order note / gift message (cart.note). Not queued behind line
     * changes (a note never conflicts with them) and sent with keepalive, so
     * a save started as the shopper taps Checkout still reaches the server.
     */
    async setNote(note) {
      const cart = await CartAPI.update({ note: String(note || '') }, { keepalive: true });
      if (CartRenderer._lastCart) CartRenderer._lastCart.note = cart.note;
      document.dispatchEvent(new CustomEvent('cart:note', { detail: { note: cart.note } }));
      return cart;
    },

    /**
     * The latest cart without a duplicate GET: the render cache, else the
     * page-load sync already in flight, else one GET.
     */
    current() {
      if (CartRenderer._lastCart) return Promise.resolve(CartRenderer._lastCart);
      if (this._initPromise) {
        return this._initPromise.then(function () {
          return CartRenderer._lastCart || CartAPI.get();
        });
      }
      return CartAPI.get();
    },

    /**
     * Initialise cart state on page load
     */
    init() {
      const p = this._sync();
      this._initPromise = p;
      return p;
    },

    async _sync() {
      /* init() re-renders from a fresh GET and is now also wired to pageshow and
         visibilitychange. Without these guards a re-sync that started before (or
         during) a mutation could land afterwards and repaint the drawer from a
         stale snapshot — e.g. wiping a just-added line back out. The mutation's
         own render is always the authoritative one. */
      if (isUpdating) return;
      const gen = cartGeneration;
      try {
        const cart = await CartAPI.get();
        /* Refuse to paint if ANY mutation started or completed while this GET was
           in flight — checking isUpdating alone misses a mutation that already
           finished and cleared the flag. */
        if (isUpdating || gen !== cartGeneration) return;
        /* Skip the repaint when nothing changed. init() also runs on pageshow
           and visibilitychange, so every app-switch back to the browser was
           rebuilding every line item and <img> for an identical cart (and
           bfcache restores double-fired). First page load always renders
           (_sig starts undefined); mutation renders bypass init and always
           repaint. Skipping is safe for the GtagEvents cache too: a matching
           signature means this exact cart was already rendered, so the cache
           (written inside render for non-empty carts) already holds an
           equivalent payload. */
        if (CartRenderer.signature(cart) === CartRenderer._sig) { CartRenderer._lastCart = cart; return; }
        CartRenderer.render(cart);
      } catch (error) {
        // Silent — cart init failure shouldn't blow up the page
      }
    },
  };

  /* --------------------------------------------------------------------------
     7. Add-to-Cart Form Binding
     -------------------------------------------------------------------------- */

  function bindAddToCartForms() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (!form) return;
      // Product page form is handled by product.js — skip to avoid duplicate add
      if (form.hasAttribute('data-product-form')) return;

      e.preventDefault();

      const variantInput = form.querySelector('[name="id"]');
      const quantityInput = form.querySelector('[name="quantity"]');

      if (!variantInput) return;

      const variantId = parseInt(variantInput.value, 10);
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      // Collect properties
      const properties = {};
      form.querySelectorAll('[name^="properties["]').forEach((input) => {
        const key = input.name.match(/properties\[(.+)\]/)?.[1];
        if (key && input.value) {
          properties[key] = input.value;
        }
      });

      // Disable button during add
      const submitBtn = form.querySelector(selectors.addToCart) || form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }

      /* .finally, not .then — addToCart rejects on a real failure, and a bare
         .then would leave the button disabled + pointer-events:none forever.
         The cart:error toast already tells the shopper what went wrong; the
         button just has to become usable again. A selling plan counts only
         from this form's own controls (no scope fallback: a grid of quick-add
         forms must never borrow another card's plan). */
      CartActions.addToCart(variantId, quantity, properties, null, sellingPlanFrom(form))
        .catch(() => {})
        .finally(() => {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-loading');
          }
        });
    });

    // Quick-add buttons (not in forms)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;

      e.preventDefault();
      const variantId = parseInt(btn.dataset.quickAdd, 10);
      if (!variantId) return;

      btn.disabled = true;
      btn.classList.add('is-loading');

      /* Same contract as the form path above: addToCart can reject, and
         `.tc-product-card__quick-add-btn.is-loading` sets pointer-events:none,
         so a bare .then would leave a dead Add button on that collection card
         until the shopper reloads the page. */
      CartActions.addToCart(variantId)
        .catch(() => {})
        .finally(() => {
          btn.disabled = false;
          btn.classList.remove('is-loading');
        });
    });
  }

  /* --------------------------------------------------------------------------
     8. Cart Error Toast
     -------------------------------------------------------------------------- */

  const CartErrorToast = {
    el: null,
    hideTimer: null,

    init() {
      // Create toast element
      this.el = document.createElement('div');
      this.el.className = 'tc-cart-toast';
      this.el.setAttribute('role', 'alert');
      this.el.setAttribute('aria-live', 'assertive');
      /* No display:none — a live region hidden from the accessibility tree at
         the moment its content is written is unreliably announced (VoiceOver/
         Safari). Visual hiding is the CSS opacity transition's job; base.css
         keeps the invisible toast click-through via pointer-events. */
      document.body.appendChild(this.el);

      // Listen for cart errors
      document.addEventListener('cart:error', (e) => {
        const msg = (e.detail && e.detail.message) || 'Something went wrong. Please try again.';
        this.show(msg);
      });
    },

    show(message) {
      if (!this.el) return;
      clearTimeout(this.hideTimer);
      /* A hide() started <300ms ago has a pending fade-clear that would blank
         this new message mid-display — cancel it. */
      clearTimeout(this.clearTimer);
      this.el.textContent = message;
      requestAnimationFrame(() => {
        this.el.classList.add('is-visible');
      });
      this.hideTimer = setTimeout(() => this.hide(), 4000);
    },

    hide() {
      if (!this.el) return;
      this.el.classList.remove('is-visible');
      this.clearTimer = setTimeout(() => {
        /* Clear AFTER the fade so the stale message leaves the a11y tree
           without emptying the toast mid-transition. */
        this.el.textContent = '';
      }, 300);
    },
  };

  /* --------------------------------------------------------------------------
     9. Initialise
     -------------------------------------------------------------------------- */

  /* Remove control on the code row (drawer and /cart). The row is rebuilt
     by the render, so focus moves somewhere stable: the drawer's discount
     toggle (never <body>, which would escape the modal), else the /cart
     summary heading. */
  function bindDiscountRemove() {
    document.addEventListener('click', (e) => {
      const rm = e.target && e.target.closest ? e.target.closest('[data-discount-remove]') : null;
      if (!rm || rm.getAttribute('aria-disabled') === 'true') return;
      e.preventDefault();
      const inDrawer = !!rm.closest(selectors.cartDrawer);
      /* aria-disabled, not disabled: disabling the focused control would
         drop keyboard focus to <body> (outside the modal drawer) while the
         request runs. */
      rm.setAttribute('aria-disabled', 'true');
      rm.textContent = 'Removing\u2026';
      const code = String(rm.getAttribute('data-code') || '').toUpperCase();
      CartActions.removeDiscount(code).then((cart) => {
        /* Failed (the toast said so) or offline: give the control back and
           don't announce a removal that didn't happen. */
        const still = !cart || ((cart.discount_codes || []).some((x) => String(x.code || '').toUpperCase() === code));
        if (still) {
          if (rm.isConnected) { rm.removeAttribute('aria-disabled'); rm.textContent = 'Remove'; }
          return;
        }
        document.dispatchEvent(new CustomEvent('cart:discount-removed'));
        const target = inDrawer
          ? document.querySelector(selectors.cartDrawer + ' [data-discount-toggle]')
          : document.querySelector('[data-cart-summary-heading]');
        if (target) {
          if (!inDrawer && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
        const live = document.querySelector(inDrawer ? selectors.cartDrawer + ' [data-discount-msg]' : '[data-cart-summary-live]');
        if (live) { live.textContent = 'Code removed.'; live.style.color = ''; }
      });
    });
  }

  /* --------------------------------------------------------------------------
     Takeover (13 Oct 2026, D1): "Switch to Subscribe & save" under a cart line

     One-off line whose variant has selling_plan_allocations (not a gift card,
     price > 0, still sellable):
       "↻ Subscribe & save 15%: £18.66 every [4 weeks ▾], delivered free [Switch]"
     A line already on a plan: "Deliver every [4 weeks ▾] · Switch to one-off".
     ", delivered free" always (19 Oct 2026: every subscription delivery
     ships free, whatever else is in the basket). Each <option> carries its
     own price (and data-free="1"). The default frequency is the plan
     already in the basket, else the first plan. Never pre-switched: the
     shopper taps Switch.

     The drawer builds its rows here (the drawer's markup is cart.js's);
     /cart prints the same markup in Liquid (snippets/subscription-delivery
     part 'switch', no layout shift) and drives it through CartSns.intent()
     / run() from its own section script, so it can swap its section HTML.
     Shown only when the drawer list carries data-sns-on (takeover switch).
     -------------------------------------------------------------------------- */
  const SnsRows = {
    _token: 0,
    _busy: false,
    _focusVid: null,
    _cache: {},

    threshold() {
      return (window.theme && window.theme.freeShippingThreshold) || config.freeShippingThreshold;
    },

    /* /products/<handle>.js (only .js has selling_plan_allocations), from
       the recommendation engine's shared cache when it is loaded.
       15 Oct 2026 fix: on a product page the engine's cache is primed with
       the "Pairs well with" first-paint JSON, a slim shape with NO selling
       plans (product-main.liquid data-pairs-json → Recs.prime). A paired
       product added to the bag (e.g. The Conditioner from a shampoo page, or
       via an upsell) then got no "Switch to Subscribe & save" row. Use the
       cached copy only when it carries selling_plan_groups (every .js
       response does, even if empty); otherwise fetch the full .js here. */
    product(handle) {
      if (!handle) return Promise.resolve(null);
      const R = window.TheColor && window.TheColor.Recs;
      if (R && typeof R.getProduct === 'function') {
        return Promise.resolve(R.getProduct(handle)).catch(() => null)
          .then((p) => (p && Array.isArray(p.selling_plan_groups) ? p : this.fetchJs(handle)));
      }
      return this.fetchJs(handle);
    },

    fetchJs(handle) {
      if (!this._cache[handle]) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        this._cache[handle] = fetchWithTimeout(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
          .then((p) => {
            if (!p) delete this._cache[handle];
            return p;
          });
      }
      return this._cache[handle];
    },

    /* "4 weeks" from the plan's option ("Deliver every 4 weeks") or name;
       the same parse as the Liquid. */
    every(plan) {
      const src = String((plan && plan.options && plan.options[0] && plan.options[0].value) || (plan && plan.name) || '');
      return src.split('every ').pop().split(',')[0].trim();
    },

    /* null when the line gets no row; otherwise { on, def, pct, opts[] }. */
    offer(item, cart, product) {
      if (!item || !product || !cart) return null;
      const variant = (product.variants || []).find((v) => Number(v.id) === Number(item.variant_id));
      const allocs = (variant && variant.selling_plan_allocations) || [];
      if (!allocs.length) return null;
      const on = !!item.selling_plan_allocation;
      if (!on && (item.gift_card || !(Number(item.original_price) > 0) || variant.available === false)) return null;
      const plans = {};
      (product.selling_plan_groups || []).forEach((g) => {
        (g.selling_plans || []).forEach((p) => { plans[String(p.id)] = p; });
      });
      const ids = allocs.map((a) => String(a.selling_plan_id));
      const others = (cart.items || []).filter((i) => i && i.key !== item.key);
      const planOf = (i) => {
        const a = i.selling_plan_allocation;
        return a ? String((a.selling_plan && a.selling_plan.id) || a.selling_plan_id || '') : '';
      };
      let def = on ? planOf(item) : '';
      if (!def) def = others.map(planOf).find((id) => id && ids.indexOf(id) > -1) || ids[0];
      const firstPlan = plans[ids[0]];
      const adj = ((firstPlan && firstPlan.price_adjustments) || []).find((a) => a && a.value_type === 'percentage');
      const pct = adj && Number(adj.value) > 0 ? Math.round(Number(adj.value) * 10) / 10 : 0;
      const opts = allocs.map((a) => {
        const id = String(a.selling_plan_id);
        const price = Number(a.price) * item.quantity;
        return { id, price, free: true, label: this.every(plans[id]) || 'delivery' };
      });
      return { on, def, pct, opts, hasSub: others.some((i) => !!i.selling_plan_allocation) };
    },

    /* Mirrors snippets/subscription-delivery.liquid part 'switch'. */
    rowHtml(item, o) {
      const esc = (t) => CartRenderer.escapeHtml(t);
      const attr = (t) => CartRenderer.escapeAttr(t);
      const title = esc(item.product_title || '');
      if (o.on) {
        const list = o.opts.map((x) => `<option value="${attr(x.id)}"${x.id === o.def ? ' selected' : ''}>${esc(x.label)}</option>`).join('');
        return '<p class="tc-cart-sns__row"><span class="tc-cart-sns__text">Deliver every ' +
          `<select class="tc-cart-sns__select" data-sns-freq aria-label="Delivery frequency">${list}</select></span>` +
          `<button type="button" class="tc-cart-sns__link" data-sns-oneoff>Switch to one-off<span class="tc-sr-only">: ${title}</span></button></p>`;
      }
      const d = o.opts.find((x) => x.id === o.def) || o.opts[0];
      const list = o.opts.map((x) => `<option value="${attr(x.id)}" data-price="${attr(CartRenderer.formatMoney(x.price))}" data-free="${x.free ? '1' : '0'}"${x.id === d.id ? ' selected' : ''}>${esc(x.label)}</option>`).join('');
      return '<p class="tc-cart-sns__row"><span class="tc-cart-sns__text"><span class="tc-cart-sns__icon" aria-hidden="true">↻</span> ' +
        `Subscribe &amp; save${o.pct > 0 ? ' ' + o.pct + '%' : ''}: <strong data-sns-price>${esc(CartRenderer.formatMoney(d.price))}</strong> every ` +
        `<select class="tc-cart-sns__select" data-sns-pick aria-label="Delivery frequency">${list}</select>` +
        `<span data-sns-free${d.free ? '' : ' hidden'}>, delivered free</span></span>` +
        `<button type="button" class="tc-cart-sns__btn" data-sns-switch>Switch<span class="tc-sr-only"> ${title} to Subscribe &amp; save</span></button></p>` +
        '<p class="tc-cart-sns__fine">' + (o.hasSub ? '' : 'Orders with Subscribe &amp; save earn no Circle points. ') +
        '<a href="/policies/subscription-policy">Subscription policy</a></p>';
    },

    /* Paint the drawer's empty slots (renderItems builds them hidden). */
    fill(cart) {
      const container = document.querySelector(selectors.cartItems);
      if (!container || !container.hasAttribute('data-sns-on') || !cart || !cart.items) return;
      const slots = container.querySelectorAll('[data-line-sns]');
      if (!slots.length) return;
      const my = ++this._token;
      const byKey = {};
      cart.items.forEach((i) => { if (i) byKey[i.key] = i; });
      Array.prototype.forEach.call(slots, (slot) => {
        const item = byKey[slot.getAttribute('data-key')];
        if (!item || !item.handle) return;
        this.product(item.handle).then((p) => {
          if (my !== this._token || !slot.isConnected) return;
          const o = this.offer(item, cart, p);
          if (!o) { slot.hidden = true; slot.textContent = ''; return; }
          slot.innerHTML = this.rowHtml(item, o);
          slot.hidden = false;
          /* Keyboard focus after a switch: the line was rebuilt (its key
             changed), so put focus back on this row's control. */
          if (this._focusVid && this._focusVid === String(item.variant_id)) {
            this._focusVid = null;
            const t = slot.querySelector('select, button');
            if (t) t.focus({ preventScroll: true });
          }
        }).catch(() => {});
      });
    },

    /* Price and ", delivered free" follow the frequency picked before Switch. */
    syncPick(sel) {
      const row = sel.closest('[data-line-sns]');
      const opt = sel.options[sel.selectedIndex];
      if (!row || !opt) return;
      const price = row.querySelector('[data-sns-price]');
      if (price) price.textContent = opt.getAttribute('data-price') || '';
      const free = row.querySelector('[data-sns-free]');
      if (free) free.hidden = opt.getAttribute('data-free') !== '1';
    },

    /* What a click/change on a row control asks for, or null. */
    intent(el, type) {
      if (!el || !el.closest) return null;
      const row = el.closest('[data-line-sns]');
      if (!row) return null;
      const labelOf = (sel, id) => {
        const o = sel && Array.prototype.find.call(sel.options, (x) => x.value === String(id));
        return o ? o.textContent.trim() : '';
      };
      let plan = null;
      let kind = '';
      let ctl = null;
      if (type === 'change') {
        ctl = el.closest('[data-sns-freq]');
        if (!ctl) return null;
        plan = ctl.value;
        kind = 'freq';
      } else {
        ctl = el.closest('[data-sns-switch], [data-sns-oneoff]');
        if (!ctl) return null;
        if (ctl.hasAttribute('data-sns-switch')) {
          const pick = row.querySelector('[data-sns-pick]');
          plan = pick ? pick.value : null;
          if (!plan) return null;
          kind = 'switch';
        } else {
          kind = 'oneoff';
        }
      }
      const sel = row.querySelector('[data-sns-pick], [data-sns-freq]');
      const label = plan ? labelOf(sel, plan) : '';
      const msg = kind === 'oneoff'
        ? 'Switched to one-off.'
        : (kind === 'switch' ? 'Switched to Subscribe & save' : 'Delivery changed') + (label ? ', every ' + label : '') + '.';
      return { row, ctl, key: row.getAttribute('data-key'), vid: row.getAttribute('data-variant-id'), plan, kind, msg };
    },

    /* Run an intent through CartActions.setSellingPlan. aria-disabled (not
       disabled) while busy, so a focused control keeps focus. */
    run(it, opts = {}) {
      if (!it || !it.key || this._busy) return Promise.resolve(null);
      this._busy = true;
      const ctls = it.row.querySelectorAll('select, button');
      ctls.forEach((c) => c.setAttribute('aria-disabled', 'true'));
      it.row.setAttribute('aria-busy', 'true');
      /* Drawer only: /cart restores focus through its own section swap. */
      if (it.ctl === document.activeElement && it.row.closest(selectors.cartDrawer)) {
        this._focusVid = it.vid;
        setTimeout(() => { this._focusVid = null; }, 3000);
      }
      return CartActions.setSellingPlan(it.key, it.plan, opts).then((cart) => {
        this._busy = false;
        if (it.row.isConnected) {
          it.row.removeAttribute('aria-busy');
          ctls.forEach((c) => c.removeAttribute('aria-disabled'));
        }
        if (!cart) this._focusVid = null;
        return cart;
      });
    },

    bind() {
      if (this._bound) return;
      this._bound = true;
      const onCartPage = (el) => !!el.closest('[data-section-type="cart-main"]');
      const drawerRun = (it) => {
        this.run(it).then((cart) => {
          if (!cart) return;
          const live = document.querySelector(selectors.cartDrawer + ' [data-sns-live]');
          if (live) live.textContent = it.msg;
        });
      };
      document.addEventListener('change', (e) => {
        const t = e.target;
        if (!t || !t.matches) return;
        if (t.matches('[data-sns-pick]')) { this.syncPick(t); return; }
        if (!t.matches('[data-sns-freq]') || onCartPage(t)) return;
        if (this._busy || t.getAttribute('aria-disabled') === 'true') return;
        drawerRun(this.intent(t, 'change'));
      });
      document.addEventListener('click', (e) => {
        const b = e.target && e.target.closest ? e.target.closest('[data-sns-switch], [data-sns-oneoff]') : null;
        if (!b || onCartPage(b)) return;
        e.preventDefault();
        if (this._busy || b.getAttribute('aria-disabled') === 'true') return;
        drawerRun(this.intent(b, 'click'));
      });
    },
  };

  function init() {
    SnsRows.bind();
    CartDrawer.init();
    const initialSync = CartActions.init();
    CartErrorToast.init();
    bindAddToCartForms();
    bindDiscountRemove();
    CartRenderer.initDispatchCountdown();

    /* view_cart for the cart PAGE itself. The drawer fires its own view_cart
       in CartDrawer.open(), but a shopper who lands directly on /cart never
       opens the drawer, so that surface reported nothing. Chained after the
       initial sync so its render has already primed the GtagEvents cache —
       no second GET needed — and routed through viewCart so a pre-consent
       load is cached and replayed by the tc:consent-granted listener rather
       than firing twice. An empty cart leaves the cache null and fires
       nothing, matching the drawer path. */
    const isCartPage = document.body.dataset.template === 'cart' ||
      window.location.pathname === '/cart';
    if (isCartPage) {
      Promise.resolve(initialSync).then(function () {
        const cart = GtagEvents._lastViewCartPayload;
        if (cart) GtagEvents.viewCart(cart);
      }).catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-sync cart state when the page is restored from the back/forward cache.
     DOMContentLoaded does NOT fire on a bfcache restore, so tapping Back left
     the header count, drawer contents, subtotal and free-delivery bar frozen
     at whatever they were when the page was cached — the shopper had to hard-
     refresh to see the truth. CartActions.init() only fetches + re-renders
     (it binds no listeners), so calling it again is safe and idempotent. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) CartActions.init();
  });

  /* Same staleness across tabs/apps: the cart can change in another tab or at
     checkout while this one sits in the background. Re-sync when it returns to
     the foreground. */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') CartActions.init();
  });

  // Expose for external use
  window.TheColor = window.TheColor || {};
  /* Silent discount pre-apply: hitting /discount/CODE makes Shopify set the
     checkout cookie, so the shopper never has to type the code — and the cart
     maths above can see it immediately. */
  window.TheColor.Discount = {
    apply(code) {
      return fetchWithTimeout('/discount/' + encodeURIComponent(code), { credentials: 'same-origin', redirect: 'manual' })
        .catch(() => {})
        .then(() => {
          /* Full re-sync: cart totals changed server-side, so subtotal, line
             prices, bar, row and badge all need the fresh truth. */
          return CartActions.init();
        });
    },
    pending() { return CartRenderer.getPendingDiscount(); },
  };
  /* Section-swap repaint: /cart replaces its own HTML with fresh Liquid after
     mutations, which paints the naive server bar over the discount-aware one.
     The section script calls this after every swap. */
  window.TheColor.CartUI = {
    repaint(cart) {
      const c = cart || CartRenderer._lastCart;
      if (c) CartRenderer.updateShippingBar(c);
      if (CartRenderer._countdownTick) CartRenderer._countdownTick();
    },
    /* For the drawer's discount field: the same verdict the code row uses. */
    codeState(code, cart) { return CartRenderer.codeState(code, cart); },
    /* The bar's free-delivery figure (19 Oct 2026): what an added one-off
       item keeps after the basket's codes (parts per million), and a gap
       turned into shelf money. product.js and tc-recs.js quote the same. */
    freeDeliveryKeep(cart) { return CartRenderer.freeKeep(cart); },
    freeDeliveryShelf(cart, gap) { return CartRenderer.freeShelf(cart, gap); },
    lineName(item) { return CartRenderer.lineName(item); },
  };
  /* Shared dispatch clock (see 3b). Never replaces one defined earlier. */
  if (!window.TheColor.Dispatch) window.TheColor.Dispatch = LocalDispatch;
  window.TheColor.Cart = CartActions;
  /* Takeover D1: /cart's section script drives its Liquid S&S rows through
     these (intent(target, 'click' | 'change') → run(intent, {sections})). */
  window.TheColor.CartSns = {
    intent(el, type) { return SnsRows.intent(el, type); },
    run(it, opts) { return SnsRows.run(it, opts); },
  };
  window.TheColor.CartDrawer = CartDrawer;
  window.TheColor.GtagEvents = GtagEvents;
})();
